import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, getAuthHeaders } from "@/api/client";
import { hasHttpStatus } from "@/lib/api-error";
import {
  vehicleDisplayName,
  vehicleOperatorDisplayName,
} from "@/lib/vehicle-display";
import { useSession } from "@/services/auth0/session";
import { useOptionalDispatchCenterScope } from "@/services/dispatch-center-scope";

export interface PageMetadata {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface OffsetPagination {
  total: number;
  limit: number;
  offset: number;
}

interface OffsetPage<T> {
  data: T[];
  pagination: OffsetPagination;
}

/** Walk an offset-paginated endpoint without duplicating termination logic. */
async function fetchAllOffsetPages<T, TResponse extends OffsetPage<T>>(
  fetchPage: (offset: number, limit: number) => Promise<TResponse>
): Promise<TResponse> {
  const pageLimit = 100;
  const rows: T[] = [];
  let pageOffset = 0;
  let total = 0;
  let firstPage: TResponse | null = null;
  do {
    const page = await fetchPage(pageOffset, pageLimit);
    firstPage ??= page;
    rows.push(...page.data);
    total = page.pagination.total;
    if (page.data.length === 0) break;
    pageOffset += page.data.length;
  } while (pageOffset < total);

  return {
    ...firstPage,
    data: rows,
    pagination: { total, limit: Math.max(total, 1), offset: 0 },
  };
}

function withDispatchCenterParam<T extends Record<string, unknown>>(
  params: T,
  dispatchCenterId: string | null
): T & { dispatchCenterId?: string } {
  return dispatchCenterId ? { ...params, dispatchCenterId } : params;
}

export type CarrierAccessRole = "CARRIER_ADMIN" | "DISPATCHER" | "OPERATOR";
export type CarrierMembershipRole =
  | "CARRIER_OWNER"
  | "CARRIER_DISPATCHER"
  | "OPERATOR";

export interface CarrierMeDispatchCenter {
  id: string;
  code: string;
  name: string;
  timeZone: string;
  isActive: boolean;
  carrierId: string;
  address: {
    city: string | null;
    province: string | null;
  } | null;
}

export interface CarrierMeResponse {
  user: {
    id: string;
    email: string;
    name: string;
    active: boolean;
  };
  roles: CarrierMembershipRole[];
  accessRoles: CarrierAccessRole[];
  capabilities: {
    canAccessCarrierWeb: boolean;
    canAccessDispatchWeb: boolean;
    canManageCarrierSettings: boolean;
    canManageTeam: boolean;
    canManagePricing: boolean;
    canManageOperators: boolean;
  };
  memberships: Array<{
    id: string;
    carrierId: string;
    carrier: { id: string; name: string | null };
    dispatchCenterId: string | null;
    dispatchCenter: { id: string; name: string | null } | null;
    role: CarrierMembershipRole;
    accessRole: CarrierAccessRole;
    scope: "CARRIER" | "DISPATCH_CENTER";
  }>;
  carriers: Array<{
    id: string;
    name: string | null;
    roles: CarrierMembershipRole[];
    accessRoles: CarrierAccessRole[];
    carrierWide: boolean;
    allowedDispatchCenters: CarrierMeDispatchCenter[];
  }>;
}

export interface CarrierCompanyProfile {
  id: string;
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  dispatchEmail: string | null;
  logoUrl: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    province: string | null;
    country: string | null;
    postalCode: string | null;
  };
}

export type CarrierCompanyProfilePatch = Partial<
  Omit<CarrierCompanyProfile, "id" | "address">
> & {
  address?: Partial<CarrierCompanyProfile["address"]>;
};

/** `GET /carrier/profile` — display + contact fields. */
export function useCompanyProfile() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "profile"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierCompanyProfile }>(
        "/profile",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/** `PATCH /carrier/profile` — owner-only on the server. */
export function useUpdateCompanyProfile() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: CarrierCompanyProfilePatch) => {
      const { data } = await api.patch<{ data: CarrierCompanyProfile }>(
        "/profile",
        patch,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "profile"] });
    },
  });
}

export interface UploadCompanyLogoVars {
  fileName: string;
  contentType: "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml";
  dataBase64: string;
  carrierId?: string;
}

/** `POST /carrier/profile/logo` — owner-only JSON/base64 logo upload. */
export function useUploadCompanyLogo() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: UploadCompanyLogoVars) => {
      const { data } = await api.post<{ data: CarrierCompanyProfile }>(
        "/profile/logo",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "profile"] });
    },
  });
}

export function useCarrierMe() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "me"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierMeResponse }>("/me", {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    enabled: isLoggedIn,
    retry: false,
  });
}

export interface CarrierAnalyticsCountRow {
  key: string;
  count: number;
}

export interface CarrierAnalytics {
  totals: {
    shipments: number;
    tasks: number;
    trips: number;
    pickupBookings: number;
    operators: number;
    vehicles: number;
    depots: number;
  };
  shipmentsByStatus: CarrierAnalyticsCountRow[];
  tasksByStatus: CarrierAnalyticsCountRow[];
  tasksByType: CarrierAnalyticsCountRow[];
  tripsByStatus: CarrierAnalyticsCountRow[];
  pickupBookingsByState: CarrierAnalyticsCountRow[];
  operatorsByStatus: CarrierAnalyticsCountRow[];
  vehiclesByStatus: CarrierAnalyticsCountRow[];
  depotsByStatus: CarrierAnalyticsCountRow[];
}

export interface CarrierTrip {
  id: string;
  tripNumber: number;
  createdAt?: string;
  status: string;
  color: string | null;
  startTime: string | null;
  endTime: string | null;
  estimatedDuration?: number | null;
  actualDuration?: number | null;
  distance?: number | null;
  startLocation?: unknown;
  endLocation?: unknown;
  plannedRoute?: unknown;
  actualRoute?: unknown;
  useCustomStartLocation?: boolean;
  useCustomEndLocation?: boolean;
  dispatchCenterId?: string;
  depotId?: string;
  vehicleId?: string | null;
  pendingTasks?: number;
  inProgressTasks?: number;
  cancelledTasks?: number;
  completedTasks?: number;
  failedTasks?: number;
  totalTasks?: number;
  operatorId: string | null;
  operator: {
    carrierUser: {
      name: string | null;
    } | null;
  } | null;
  vehicle?: {
    id: string;
    code: string;
    make: string | null;
    model: string | null;
    licensePlate: string;
    status: string;
    capacity?: unknown;
  } | null;
  depot?: {
    id: string;
    name: string;
    code: string;
  } | null;
  dispatchCenter?: {
    id: string;
    name: string;
    code: string;
  } | null;
  tasks?: Array<{
    id: string;
    taskNumber?: number;
    type: string;
    status: string;
    shipmentSnapshot?: unknown;
  }>;
  waypoints?: Array<{
    id: string;
    sequence: number;
    locationKey: string | null;
    mapLocation?: { lat: number; lng: number } | null;
    eta?: CarrierWaypointEta | null;
    tasks?: CarrierTripStopTask[];
  }>;
}

export interface CarrierTripsResponse {
  data: CarrierTrip[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/** Parse a waypoint locationKey ("lat,lng") to `[lat, lng]` (or null). */
export function parseLocationKey(
  key: string | null | undefined
): [number, number] | null {
  if (!key) return null;
  const parts = key.split(",");
  if (parts.length !== 2) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return [lat, lng];
}

export interface CarrierTask {
  id: string;
  taskNumber: number;
  type: string;
  status: string;
  description: string | null;
  contactName: string | null;
  contactCompany?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  createdAt: string;
  updatedAt?: string;
  scheduledDate?: string | null;
  schedulingTimeZone?: string | null;
  scheduledWindow?: {
    slotId: string;
    date: string;
    startTime: string;
    endTime: string;
    timeZone: string;
    startAt: string;
    endAt: string;
  } | null;
  readyAt?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  receivedAt: string | null;
  dispatchCenterId: string | null;
  carrierId: string | null;
  operatorId: string | null;
  shipmentId: string | null;
  waypointId: string | null;
  tripId: string | null;
  addressId: string | null;
  parentTaskId?: string | null;
  failureReason?: string | null;
  failureReasonCode?: number | null;
  notes?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressProvince?: string | null;
  addressCountry?: string | null;
  addressPostalCode?: string | null;
  addressInstructions?: string | null;
  shipment: {
    id: string;
    orderId: string;
    orderNumber?: number;
    shipmentNumber: number;
    deliveryNotes?: string | null;
    trackingNumber: string | null;
    addresses?: Array<{
      type: "FROM" | "TO";
      contactName: string | null;
      contactCompany: string | null;
    }>;
    status?: string;
    carrier?: { code: string | null; name: string | null } | null;
    customer?: {
      firstName: string | null;
      lastName: string | null;
    } | null;
    packageCount?: number;
    cancellationRequestedAt?: string | null;
  } | null;
  dispatchCenter: {
    id: string;
    name: string;
    code: string | null;
  } | null;
  /** Transitional compatibility for older task payloads. */
  depot?: {
    id: string;
    name: string;
    code: string;
  } | null;
  trip?: {
    id: string;
    tripNumber: number;
    status: string;
    color: string | null;
    startTime: string | null;
    endTime: string | null;
    operator: {
      id: string;
      carrierUser: {
        name: string | null;
        email: string | null;
      } | null;
    } | null;
  } | null;
  operator?: {
    id: string;
    status: string;
    carrierUser: {
      name: string | null;
      email: string | null;
    } | null;
  } | null;
  parentTask?: {
    id: string;
    taskNumber: number;
    type: string;
    status: string;
  } | null;
  childTasks?: Array<{
    id: string;
    taskNumber: number;
    type: string;
    status: string;
  }>;
  proof?: {
    id: string;
    notes: string | null;
    createdAt: string;
    mediaItems: Array<{
      id: string;
      type: string;
      url: string;
      key: string;
    }>;
  } | null;
  shipmentSnapshot?: unknown;
  mapLocation?: { lat: number; lng: number } | null;
  address?: {
    id: string;
    line1: string;
    line2?: string | null;
    city: string;
    province?: string;
    country?: string;
    postalCode?: string | null;
    // `{ lat, lng }` or GeoJSON `{ coordinates: [lng, lat] }`.
    location: unknown;
  } | null;
  cancellationReviewPending?: boolean;
  workHold?: {
    reason: "CANCELLATION_REVIEW" | string;
    requestedAt?: string | null;
  } | null;
}

/** Normalize an Address.location JSON blob to `{ lat, lng }` (or null). */
export function taskLatLng(
  task: CarrierTask
): { lat: number; lng: number } | null {
  if (task.mapLocation) return task.mapLocation;
  const loc = task.address?.location as
    | { lat?: number; lng?: number; coordinates?: [number, number] }
    | null
    | undefined;
  if (!loc) return null;
  if (typeof loc.lat === "number" && typeof loc.lng === "number") {
    return { lat: loc.lat, lng: loc.lng };
  }
  if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
    return { lat: loc.coordinates[1], lng: loc.coordinates[0] };
  }
  return null;
}

export interface CarrierTasksResponse {
  data: CarrierTask[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Operator row as returned by `GET /api/v2/carrier/operators`. Mirrors the
 * server handler's `include`d shape — see get-operators.handler.ts.
 */
export interface CarrierOperator {
  id: string;
  carrierId?: string | null;
  depotId?: string | null;
  dispatchCenterId: string | null;
  status: string;
  dateOfBirth: string | null;
  carrierUser?: {
    id: string;
    email: string | null;
    name: string | null;
    phone: string | null;
  } | null;
  /** Legacy response name retained while older deployments roll forward. */
  depot?: {
    id: string;
    name: string;
    carrierId: string;
    dispatchCenterId: string | null;
  } | null;
  /** Current carrier endpoint relation name. */
  dispatchCenter: {
    id: string;
    name: string;
    pickupProviderCarrierId?: string;
  } | null;
  vehicle: {
    id: string;
    code: string;
    licensePlate: string | null;
    status: string;
    capacity?: unknown;
  } | null;
  // Last heartbeat position: `{ lat, lng }` or GeoJSON `{ coordinates }`.
  lastLocation?: unknown;
  mapLocation?: { lat: number; lng: number } | null;
  lastLocationTime?: string | null;
  isTracking?: boolean;
  carrierInvite?: {
    id: string;
    email: string;
    expiresAt: string;
    acceptedAt: string | null;
    dispatchCenterId: string | null;
  } | null;
  tasks?: Array<{
    id: string;
    type: string;
    status: string;
    createdAt: string;
  }>;
  trips?: Array<{
    id: string;
    status: string;
    startTime: string | null;
    endTime: string | null;
  }>;
  shifts?: Array<{
    id: string;
    date: string;
    shiftType: string;
    startTime: string | null;
    endTime: string | null;
    status: string;
    notes: string | null;
  }>;
}

/** Normalize an operator's last heartbeat location to `{ lat, lng }` (or null). */
export function operatorLatLng(
  op: CarrierOperator
): { lat: number; lng: number } | null {
  if (op.mapLocation) return op.mapLocation;
  const loc = op.lastLocation as
    | { lat?: number; lng?: number; coordinates?: [number, number] }
    | null
    | undefined;
  if (!loc) return null;
  if (typeof loc.lat === "number" && typeof loc.lng === "number") {
    return { lat: loc.lat, lng: loc.lng };
  }
  if (Array.isArray(loc.coordinates) && loc.coordinates.length === 2) {
    return { lat: loc.coordinates[1], lng: loc.coordinates[0] };
  }
  return null;
}

export interface CarrierOperatorsResponse {
  data: CarrierOperator[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CarrierVehicle {
  id: string;
  code: string;
  licensePlate: string | null;
  type: string;
  status: string;
  make: string | null;
  model: string | null;
  year: number | null;
  vin: string | null;
  capacity: unknown;
  /** Current carrier endpoint relation name. */
  dispatchCenter?: {
    id: string;
    name: string;
    pickupProviderCarrierId?: string;
  } | null;
  /** Legacy response name retained while older deployments roll forward. */
  depot?: {
    id: string;
    name: string;
    carrierId: string;
    dispatchCenterId: string | null;
  } | null;
  operator: {
    id: string;
    carrierUser: {
      name: string | null;
    } | null;
  } | null;
}

export interface CarrierVehiclesResponse {
  data: CarrierVehicle[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Operational analytics — `GET /api/v2/carrier/analytics`.
 * Read-only aggregate counts, carrier scope implicit server-side.
 */
export function useCarrierAnalytics() {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "analytics", { dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierAnalytics }>("/analytics", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam({}, dispatchCenterId),
      });
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Operator-efficiency report — `GET /api/v2/carrier/analytics/operator-efficiency`.
 */
export interface CarrierOperatorEfficiencyRow {
  operatorId: string;
  operatorName: string;
  depot: string;
  tasks: {
    total: number;
    completed: number;
    failed: number;
  };
  successRate: number;
  efficiencyScore: number;
  avgTaskTimeMinutes: number | null;
  trend: "up" | "down" | "stable";
}

export interface CarrierOperatorEfficiency {
  operators: CarrierOperatorEfficiencyRow[];
  benchmarks: {
    avgTasksPerOperator: number;
    avgTaskTimeMinutes: number;
    avgSuccessRate: number;
  };
  period: { start: string; end: string };
}

export interface CarrierAlertTask {
  id: string;
  taskNumber: number;
  shipmentNumber: number | null;
  reason?: string;
  status?: string;
  completedAt?: string;
  openedAt?: string;
}

export interface CarrierAlerts {
  failedToday: {
    count: number;
    breakdown: Record<string, number>;
    tasks: CarrierAlertTask[];
  };
  stuckTasks: {
    count: number;
    tasks: CarrierAlertTask[];
  };
}

/** `GET /carrier/analytics/alerts` — failed-today + aging backlog. */
export function useCarrierAlerts() {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "alerts", { dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<CarrierAlerts>("/analytics/alerts", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam({}, dispatchCenterId),
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

export interface CarrierScorecard {
  date: string;
  completedToday: number;
  failedToday: number;
  openTasks: number;
  receivedToday: number;
  successRate: number;
  completedYesterday: number;
  trend: "up" | "down" | "stable";
}

/** `GET /carrier/analytics/scorecard` — today's operational throughput. */
export function useCarrierScorecard() {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "scorecard", { dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<CarrierScorecard>("/analytics/scorecard", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam({}, dispatchCenterId),
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

export function useCarrierOperatorEfficiency(params: {
  startDate: string;
  endDate: string;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "operator-efficiency", params, { dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<CarrierOperatorEfficiency>(
        "/analytics/operator-efficiency",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(params, dispatchCenterId),
        }
      );
      return data;
    },
    enabled: isLoggedIn && !!params.startDate && !!params.endDate,
  });
}

/**
 * Trips list — `GET /api/v2/carrier/trips`. Scope implicit.
 */
export function useCarrierTrips(params?: {
  status?: string;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const status = params?.status;
  const q = params?.q?.trim() ?? "";
  const dateFrom = params?.dateFrom ?? "";
  const dateTo = params?.dateTo ?? "";
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [
      "carrier",
      "trips",
      {
        status: status ?? null,
        q: q || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        page,
        limit,
        dispatchCenterId,
      },
    ],
    queryFn: async () => {
      const fetchPage = async (
        requestedPage: number,
        requestedLimit: number
      ) => {
        const { data } = await api.get<CarrierTripsResponse>("/trips", {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(
            {
              page: requestedPage,
              limit: requestedLimit,
              ...(status && { status }),
            },
            dispatchCenterId
          ),
        });
        return {
          ...data,
          data: data.data.map(normalizeCarrierTrip),
        };
      };

      if (!q && !dateFrom && !dateTo) return fetchPage(page, limit);

      // The carrier Trip endpoint only supports scope, status, and pagination.
      // Aggregate the complete scoped result before applying the UI's search
      // and date filters so a match is never limited to the current page.
      const searchPageSize = 200;
      const firstPage = await fetchPage(1, searchPageSize);
      const allTrips = [...firstPage.data];
      for (
        let requestedPage = 2;
        requestedPage <= firstPage.metadata.totalPages;
        requestedPage += 1
      ) {
        const nextPage = await fetchPage(requestedPage, searchPageSize);
        allTrips.push(...nextPage.data);
      }

      const normalizedQuery = q.toLocaleLowerCase();
      const matches = allTrips.filter((trip) => {
        const tripDate = trip.startTime ?? trip.createdAt ?? trip.endTime;
        const parsedTripDate = tripDate ? new Date(tripDate) : null;
        const day =
          parsedTripDate && !Number.isNaN(parsedTripDate.getTime())
            ? parsedTripDate.toISOString().slice(0, 10)
            : "";
        if (dateFrom && (!day || day < dateFrom)) return false;
        if (dateTo && (!day || day > dateTo)) return false;
        if (!normalizedQuery) return true;
        const operatorName = trip.operator?.carrierUser?.name ?? "";
        const vehicleName = [
          trip.vehicle?.code,
          trip.vehicle?.licensePlate,
          trip.vehicle?.make,
          trip.vehicle?.model,
        ]
          .filter(Boolean)
          .join(" ");
        return [
          `TRP-${trip.tripNumber}`,
          trip.id,
          trip.status,
          operatorName,
          vehicleName,
          trip.dispatchCenter?.name,
          trip.depot?.name,
        ].some((value) =>
          String(value ?? "")
            .toLocaleLowerCase()
            .includes(normalizedQuery)
        );
      });
      const totalPages = Math.ceil(matches.length / limit);
      const offset = (page - 1) * limit;
      return {
        data: matches.slice(offset, offset + limit),
        metadata: {
          total: matches.length,
          page,
          limit,
          totalPages,
        },
      };
    },
    enabled: isLoggedIn,
  });
}

function normalizeCarrierTrip(trip: CarrierTrip): CarrierTrip {
  if (trip.depot || !trip.dispatchCenter) return trip;
  return { ...trip, depot: trip.dispatchCenter };
}

/**
 * Tasks list — `GET /api/v2/carrier/tasks` (exists since #249). Scope is
 * implicit; the server applies it via task carrier/dispatch-center columns.
 * The server accepts `page`/`limit`/`status`/`type` plus receipt queue filters
 * (see get-tasks.handler.ts) and returns the paginated envelope verbatim.
 */
interface CarrierTaskFilters {
  awaitingReceipt?: boolean;
  excludeAwaitingReceipt?: boolean;
  enabled?: boolean;
  status?: string;
  type?: string;
  q?: string;
  tripStatus?: string;
}

type CarrierTaskPageParams = CarrierTaskFilters & {
  page?: number;
  limit?: number;
};

async function fetchCarrierTasksPage(args: {
  token: string | null;
  dispatchCenterId: string | null;
  filters: CarrierTaskFilters;
  page: number;
  limit: number;
}) {
  const { token, dispatchCenterId, filters, page, limit } = args;
  const q = filters.q?.trim();
  const { data } = await api.get<CarrierTasksResponse>("/tasks", {
    headers: getAuthHeaders(token),
    params: withDispatchCenterParam(
      {
        page,
        limit,
        ...(filters.awaitingReceipt ? { awaitingReceipt: true } : {}),
        ...(filters.excludeAwaitingReceipt
          ? { excludeAwaitingReceipt: true }
          : {}),
        ...(filters.status && { status: filters.status }),
        ...(filters.type && { type: filters.type }),
        ...(q && { q }),
        ...(filters.tripStatus && { tripStatus: filters.tripStatus }),
      },
      dispatchCenterId
    ),
  });
  return data;
}

export function useCarrierTasks(params?: CarrierTaskPageParams) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [
      "carrier",
      "tasks",
      { ...params, page, limit, dispatchCenterId },
    ],
    queryFn: () =>
      fetchCarrierTasksPage({
        token,
        dispatchCenterId,
        filters: params ?? {},
        page,
        limit,
      }),
    enabled: isLoggedIn && (params?.enabled ?? true),
  });
}

export function useCarrierTaskBacklog(
  params?: CarrierTaskFilters & { limit?: number }
) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const limit = params?.limit ?? 200;
  return useQuery({
    queryKey: [
      "carrier",
      "task-backlog",
      { ...params, limit, dispatchCenterId },
    ],
    queryFn: async () => {
      const firstPage = await fetchCarrierTasksPage({
        token,
        dispatchCenterId,
        filters: params ?? {},
        page: 1,
        limit,
      });
      const tasks = [...firstPage.data];
      for (
        let requestedPage = 2;
        requestedPage <= firstPage.metadata.totalPages;
        requestedPage += 1
      ) {
        const nextPage = await fetchCarrierTasksPage({
          token,
          dispatchCenterId,
          filters: params ?? {},
          page: requestedPage,
          limit,
        });
        tasks.push(...nextPage.data);
      }
      return {
        data: tasks,
        metadata: {
          total: tasks.length,
          page: 1,
          limit: Math.max(tasks.length, 1),
          totalPages: tasks.length > 0 ? 1 : 0,
        },
      };
    },
    enabled: isLoggedIn && (params?.enabled ?? true),
  });
}

export function useCarrierTask(id: string | null | undefined) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "tasks", id],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierTask }>(`/tasks/${id}`, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    enabled: isLoggedIn && !!id,
  });
}

/**
 * Operators list — `GET /api/v2/carrier/operators` (server #250).
 */
export function useCarrierOperators(params?: {
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const status = params?.status;
  const q = params?.q;
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [
      "carrier",
      "operators",
      { status: status ?? null, q: q ?? null, page, limit, dispatchCenterId },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierOperatorsResponse>("/operators", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam(
          { page, limit, ...(status && { status }), ...(q && { q }) },
          dispatchCenterId
        ),
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

export function useCarrierOperator(id: string | null | undefined) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "operators", id],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierOperator }>(
        `/operators/${id}`,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!id,
  });
}

export interface CreateCarrierOperatorVars {
  carrierId?: string;
  dispatchCenterId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  dateOfBirth: string;
  documents?: Record<string, unknown>;
  workSchedule?: unknown;
}

export function useCreateCarrierOperator() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateCarrierOperatorVars) => {
      const { data } = await api.post<{
        operator: CarrierOperator;
        invite: CarrierTeamInvite;
      }>("/operators", vars, { headers: getAuthHeaders(token) });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
      qc.invalidateQueries({ queryKey: ["carrier", "team-invites"] });
    },
  });
}

/**
 * Vehicles list — `GET /api/v2/carrier/vehicles` (server #250).
 */
export function useCarrierVehicles(params?: {
  status?: string;
  q?: string;
  page?: number;
  limit?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const status = params?.status;
  const q = params?.q?.trim() ?? "";
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [
      "carrier",
      "vehicles",
      { status: status ?? null, q: q || null, page, limit, dispatchCenterId },
    ],
    queryFn: async () => {
      const fetchPage = async (
        requestedPage: number,
        requestedLimit: number
      ) => {
        const { data } = await api.get<CarrierVehiclesResponse>("/vehicles", {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(
            {
              page: requestedPage,
              limit: requestedLimit,
              ...(status && { status }),
            },
            dispatchCenterId
          ),
        });
        return data;
      };

      if (!q) return fetchPage(page, limit);

      // The existing carrier endpoint has no q contract. Search the complete
      // scoped fleet through its live paginated API instead of pretending the
      // current page is the whole roster or inventing client-side records.
      const searchPageSize = Math.max(limit, 100);
      const firstPage = await fetchPage(1, searchPageSize);
      const allVehicles = [...firstPage.data];
      for (
        let requestedPage = 2;
        requestedPage <= firstPage.metadata.totalPages;
        requestedPage += 1
      ) {
        const nextPage = await fetchPage(requestedPage, searchPageSize);
        allVehicles.push(...nextPage.data);
      }
      const normalizedQuery = q.toLocaleLowerCase();
      const matches = allVehicles.filter((vehicle) =>
        [
          vehicle.code,
          vehicle.licensePlate,
          vehicle.type,
          vehicle.status,
          vehicle.make,
          vehicle.model,
          vehicle.year,
          vehicle.vin,
          vehicle.dispatchCenter?.name,
          vehicle.depot?.name,
          vehicle.operator?.carrierUser?.name,
          vehicleDisplayName(vehicle),
          vehicleOperatorDisplayName(vehicle),
          safeJsonString(vehicle.capacity),
        ]
          .filter((value) => value !== null && value !== undefined)
          .some((value) =>
            String(value).toLocaleLowerCase().includes(normalizedQuery)
          )
      );

      return {
        data: matches,
        metadata: {
          total: matches.length,
          page: 1,
          limit: Math.max(matches.length, 1),
          totalPages: matches.length > 0 ? 1 : 0,
        },
      };
    },
    enabled: isLoggedIn,
  });
}

function safeJsonString(value: unknown): string {
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}

/**
 * Assign an operator to a trip — `POST /trips/:id/assign-operator`. Server
 * cascades the operator down to the trip's waypoint tasks too, so we
 * invalidate both trips + tasks on success.
 */
export interface AssignOperatorToTripVars {
  tripId: string;
  operatorId: string;
}

export function useAssignOperatorToTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, operatorId }: AssignOperatorToTripVars) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/assign-operator`,
        { operatorId },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Create a trip on one of the caller's dispatch centers — `POST /trips`.
 */
export interface CreateCarrierTripVars {
  dispatchCenterId: string;
  color?: string;
}

export function useCreateCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dispatchCenterId, color }: CreateCarrierTripVars) => {
      const { data } = await api.post<CarrierTrip>(
        "/trips",
        { dispatchCenterId, ...(color !== undefined && { color }) },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

/**
 * Archive a COMPLETED trip — `PATCH /trips/:id/archive`.
 */
export function useArchiveCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await api.patch<{ message: string }>(
        `/trips/${tripId}/archive`,
        undefined,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, tripId) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Clear the driver from a trip — `POST /trips/:id/unassign-operator`.
 * Server clears the operator on the trip and its tasks, so invalidate both.
 */
export function useUnassignTripOperator() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/unassign-operator`,
        undefined,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, tripId) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Assign or clear a vehicle on a trip — `POST /trips/:id/assign-vehicle`.
 */
export function useAssignVehicleToTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tripId,
      vehicleId,
    }: {
      tripId: string;
      vehicleId: string | null;
    }) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/assign-vehicle`,
        { vehicleId },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "vehicles"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Start a PLANNED trip — `POST /trips/:id/start`.
 */
export function useStartCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/start`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, tripId) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Delete a non-completed trip and reset its tasks — `DELETE /trips/:id`.
 */
export function useDeleteCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tripId: string) => {
      const { data } = await api.delete<{ message: string }>(
        `/trips/${tripId}`,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, tripId) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.removeQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Update a trip's display color — `PATCH /trips/:id`.
 */
export interface UpdateCarrierTripVars {
  tripId: string;
  color: string;
}

export function useUpdateCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, color }: UpdateCarrierTripVars) => {
      const { data } = await api.patch<{ message: string }>(
        `/trips/${tripId}`,
        { color },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_data, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Assign tasks to a trip — `POST /trips/:id/assign-tasks`. Server groups them
 * into waypoints; a single out-of-scope task fails the whole call.
 */
export interface TripTasksVars {
  tripId: string;
  taskIds: string[];
  verifiedBuildingVisits?: Array<{
    taskIds: string[];
    reviewedTaskIds: string[];
    waypointId?: string;
    anchorTaskId?: string;
    confirmed: true;
  }>;
}

export function useAssignTasksToTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tripId,
      taskIds,
      verifiedBuildingVisits,
    }: TripTasksVars) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/assign-tasks`,
        {
          taskIds,
          ...(verifiedBuildingVisits?.length ? { verifiedBuildingVisits } : {}),
        },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_d, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Remove tasks from a trip — `POST /trips/:id/remove-tasks`.
 */
export function useRemoveTasksFromTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, taskIds }: TripTasksVars) => {
      const { data } = await api.post<{ message: string }>(
        `/trips/${tripId}/remove-tasks`,
        { taskIds },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_d, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
    },
  });
}

/**
 * Trip detail (waypoints/stops + tasks) — `GET /trips/:id`.
 */
export interface CarrierTripStopTask {
  id: string;
  mapLocation?: { lat: number; lng: number } | null;
  type: string;
  status: string;
  taskNumber: number;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressCity?: string | null;
  addressProvince?: string | null;
  addressInstructions?: string | null;
  shipmentNumber?: number | null;
  shipmentSnapshot?: unknown;
  /** Who is being visited — the name to say on the phone. */
  contactName?: string | null;
  contactPhone?: string | null;
  /** Historical Task assignment note; current delivery text is on Shipment. */
  notes?: string | null;
  shipment?: {
    shipmentNumber?: number | null;
    deliveryNotes?: string | null;
  } | null;
  /** Any package on the shipment is marked fragile. */
  fragile?: boolean;
  /**
   * The booked pickup window. `null` on tasks that carry none — which is most
   * of them, since only pickups are booked into a slot.
   */
  scheduledWindow?: {
    slotId: string;
    date: string;
    startTime: string;
    endTime: string;
  } | null;
  cancellationReviewPending?: boolean;
  workHold?: {
    reason: "CANCELLATION_REVIEW" | string;
    requestedAt?: string | null;
  } | null;
}

export interface CarrierWaypointEta {
  estimatedArrivalTime: string;
  estimatedDepartureTime: string;
  actualArrivalTime: string | null;
  actualDepartureTime: string | null;
}

export interface CarrierTripStop {
  id: string;
  sequence: number;
  locationKey: string;
  mapLocation?: { lat: number; lng: number } | null;
  eta?: CarrierWaypointEta | null;
  tasks: CarrierTripStopTask[];
}

export interface CarrierTripDetail {
  id: string;
  tripNumber: number;
  createdAt: string;
  status: string;
  color: string | null;
  startTime: string | null;
  endTime: string | null;
  estimatedDuration: number | null;
  actualDuration: number | null;
  distance: number | null;
  startLocation: unknown;
  endLocation: unknown;
  plannedRoute: unknown;
  actualRoute: unknown;
  useCustomStartLocation: boolean;
  useCustomEndLocation: boolean;
  dispatchCenterId: string;
  vehicleId: string | null;
  operatorId: string | null;
  pendingTasks?: number;
  inProgressTasks?: number;
  cancelledTasks?: number;
  completedTasks: number;
  failedTasks: number;
  totalTasks: number;
  operator: {
    id: string;
    name: string | null;
    picture?: string | null;
  } | null;
  vehicle: {
    id: string;
    code: string;
    make: string | null;
    model: string | null;
    licensePlate: string;
    status: string;
    capacity?: unknown;
  } | null;
  depot: { id: string; name: string; code: string } | null;
  tasks: CarrierTripStopTask[];
  waypoints: CarrierTripStop[];
}

export function useCarrierTripDetail(tripId: string | null) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier", "trip", tripId],
    queryFn: async () => {
      const { data } = await api.get<CarrierTripDetail>(`/trips/${tripId}`, {
        headers: getAuthHeaders(token),
      });
      return data;
    },
    enabled: !!tripId,
  });
}

/**
 * Reorder a trip's stops — `PATCH /trips/:id/reorder-stops`.
 */
export interface ReorderTripStopsVars {
  tripId: string;
  waypointOrders: Array<{ waypointId: string; sequence: number }>;
}

export function useReorderTripStops() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, waypointOrders }: ReorderTripStopsVars) => {
      const { data } = await api.patch<{ message: string }>(
        `/trips/${tripId}/reorder-stops`,
        { waypointOrders },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_d, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

/**
 * Remove a stop (waypoint) from a trip —
 * `DELETE /trips/:id/waypoints/:waypointId`.
 */
export interface RemoveTripStopVars {
  tripId: string;
  waypointId: string;
}

export function useRemoveTripStop() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, waypointId }: RemoveTripStopVars) => {
      const { data } = await api.delete<{ message: string }>(
        `/trips/${tripId}/waypoints/${waypointId}`,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_d, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
    },
  });
}

export interface CarrierDispatchTimeWindow {
  startTime: string;
  endTime: string;
  slotIds: string[];
}

export interface CarrierDispatchLookupTrip {
  id: string;
  tripNumber: number;
  status: string;
  color: string | null;
  totalTasks: number;
  driverName: string | null;
  dispatchCenterName: string | null;
}

export interface CarrierDispatchLookupTask {
  id: string;
  taskNumber: number;
  type: string;
  status: string;
  tripId: string | null;
  shipmentId: string | null;
  shipmentNumber: number | null;
  trackingNumber: string | null;
  location: string | null;
  dispatchCenterName: string | null;
}

export interface CarrierDispatchLookupShipment {
  id: string;
  shipmentNumber: number;
  trackingNumber: string | null;
  status: string;
  orderId: string;
  orderNumber: number | null;
  tripId: string | null;
}

export interface CarrierDispatchLookupDriver {
  id: string;
  name: string;
  picture: string | null;
  status: string;
  dispatchCenterName: string | null;
  vehicleCode: string | null;
}

export interface CarrierDispatchLookupCustomer {
  id: string;
  customerNumber: number;
  name: string;
  email: string | null;
  phone: string | null;
  companyName: string | null;
  orderId: string;
  orderNumber: number;
}

export interface CarrierDispatchLookupResponse {
  trips: CarrierDispatchLookupTrip[];
  tasks: CarrierDispatchLookupTask[];
  shipments: CarrierDispatchLookupShipment[];
  drivers: CarrierDispatchLookupDriver[];
  customers: CarrierDispatchLookupCustomer[];
}

/** One minimal, server-authorized lookup projection for the Dispatch workspace. */
export function useCarrierDispatchLookup(
  query: string,
  options?: { enabled?: boolean; limit?: number }
) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const q = query.trim();
  const limit = options?.limit ?? 8;
  return useQuery({
    queryKey: ["carrier", "dispatch", "lookup", { q, limit, dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<{
        data: CarrierDispatchLookupResponse;
      }>("/dispatch/lookup", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam({ q, limit }, dispatchCenterId),
      });
      return data.data;
    },
    enabled: isLoggedIn && Boolean(q) && (options?.enabled ?? true),
  });
}

export function useCarrierDispatchTimeWindows() {
  const { token } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "dispatch", "time-windows", dispatchCenterId],
    queryFn: async () => {
      const { data } = await api.get<{
        data: CarrierDispatchTimeWindow[];
      }>("/dispatch/time-windows", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam({}, dispatchCenterId),
      });
      return data.data;
    },
  });
}

export interface ComputeCarrierTripRouteVars {
  tripId: string;
  startTime?: string;
}

export function useComputeCarrierTripRoute() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId, startTime }: ComputeCarrierTripRouteVars) => {
      const { data } = await api.get<{
        message: string;
        data: CarrierTripDetail;
      }>(`/trips/${tripId}/route/compute`, {
        headers: getAuthHeaders(token),
        params: startTime ? { startTime } : undefined,
      });
      return data;
    },
    onSuccess: (response, { tripId }) => {
      qc.setQueryData(["carrier", "trip", tripId], response.data);
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

export interface CarrierRouteLocation {
  lat: number;
  lng: number;
  address?: string;
}

export interface UpdateCarrierTripRoutingDetailsVars {
  tripId: string;
  useCustomStartLocation: boolean;
  startLocation: CarrierRouteLocation | null;
  useCustomEndLocation: boolean;
  endLocation: CarrierRouteLocation | null;
}

export function useUpdateCarrierTripRoutingDetails() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tripId,
      ...body
    }: UpdateCarrierTripRoutingDetailsVars) => {
      const { data } = await api.patch<{ message: string }>(
        `/trips/${tripId}/routing-details`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: (_response, { tripId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

export interface CarrierTripWaypointDetail {
  id: string;
  tripId: string;
  sequence: number;
  radius: number | null;
  locationKey: string;
  mapLocation: { lat: number; lng: number } | null;
  eta: CarrierWaypointEta | null;
  tasks: CarrierTask[];
}

export interface SplitCarrierStopInput {
  tripId: string;
  waypointId: string;
  taskId: string;
  sourceTaskIds: string[];
  sourceAnchorTaskId: string;
  expectedSourcePin: string;
  expectedMovedPin: string;
  expectedRemainingPin: string;
}

export function useSplitCarrierStop() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tripId,
      waypointId,
      ...body
    }: SplitCarrierStopInput) => {
      const { data } = await api.post<{ routeNeedsRefresh: boolean }>(
        `/trips/${tripId}/waypoints/${waypointId}/split`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: async (_result, { tripId }) => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] }),
        qc.invalidateQueries({ queryKey: ["carrier", "trips"] }),
      ]);
    },
  });
}

export function useCarrierTripWaypoint(
  tripId: string | null,
  waypointId: string | null
) {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier", "trip", tripId, "waypoint", waypointId],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierTripWaypointDetail }>(
        `/trips/${tripId}/waypoints/${waypointId}`,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: Boolean(tripId && waypointId),
  });
}

export interface StartCarrierTripWaypointVars {
  tripId: string;
  waypointId: string;
}

export function useStartCarrierTripWaypoint() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tripId,
      waypointId,
    }: StartCarrierTripWaypointVars) => {
      const { data } = await api.post<{
        message: string;
        data: { startedTaskIds: string[] };
      }>(`/trips/${tripId}/waypoints/${waypointId}/start`, undefined, {
        headers: getAuthHeaders(token),
      });
      return data;
    },
    onSuccess: (_response, { tripId, waypointId }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "trip", tripId] });
      qc.invalidateQueries({
        queryKey: ["carrier", "trip", tripId, "waypoint", waypointId],
      });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

export function useCompleteCarrierTrip() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tripId }: { tripId: string }) => {
      const { data } = await api.post<{
        message: string;
        data: CarrierTripDetail;
      }>(`/trips/${tripId}/complete`, undefined, {
        headers: getAuthHeaders(token),
      });
      return data;
    },
    onSuccess: (response, { tripId }) => {
      qc.setQueryData(["carrier", "trip", tripId], response.data);
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
    },
  });
}

/**
 * Mark a customer drop-off received at a center — `POST /tasks/:id/receive`.
 */
export interface ReceiveTaskVars {
  taskId: string;
  dispatchCenterId?: string;
}

export function useReceiveTask() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, dispatchCenterId }: ReceiveTaskVars) => {
      const { data } = await api.post<{ data: { receivedAt: string | null } }>(
        `/tasks/${taskId}/receive`,
        dispatchCenterId ? { dispatchCenterId } : {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
    },
  });
}

export interface ReceiveTasksBulkVars {
  taskIds: string[];
  dispatchCenterId: string;
}

export function useReceiveTasksBulk() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReceiveTasksBulkVars) => {
      const { data } = await api.post<{
        data: {
          received: number;
          taskIds: string[];
          dispatchCenterId: string;
        };
      }>("/tasks/bulk-receive", input, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
    },
  });
}

export interface FailCarrierTaskVars {
  taskId: string;
  reason: string;
  reasonCode: number;
  notes?: string;
  media?: Array<{
    data: string;
    type: "photo" | "video";
    mimeType: string;
  }>;
}

export function useFailCarrierTask() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, ...input }: FailCarrierTaskVars) => {
      const { data } = await api.patch<{
        data: {
          id: string;
          status: string;
          failureReason: string | null;
          failureReasonCode: number | null;
          outcome:
            | "RETRY_CREATED"
            | "CANCELLATION_REQUESTED"
            | "FINAL_FAILURE"
            | "FAILED";
          retryTaskIds: string[];
        };
      }>(`/tasks/${taskId}/fail`, input, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trip"] });
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
      qc.invalidateQueries({ queryKey: ["carrier", "alerts"] });
    },
  });
}

/**
 * Complete a carrier-scoped task — `POST /tasks/:id/complete`.
 */
export function useCompleteCarrierTask() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId }: { taskId: string }) => {
      const { data } = await api.post<{ data: CarrierTask }>(
        `/tasks/${taskId}/complete`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
    },
  });
}

/**
 * Reassign a task to a different dispatch center — `POST /tasks/:id/reassign`.
 */
export interface ReassignTaskVars {
  taskId: string;
  dispatchCenterId: string;
  reason?: string;
}

export function useReassignTask() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      dispatchCenterId,
      reason,
    }: ReassignTaskVars) => {
      const { data } = await api.post<{ message: string }>(
        `/tasks/${taskId}/reassign`,
        { dispatchCenterId, ...(reason !== undefined && { reason }) },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
    },
  });
}

export interface AssignCarrierTaskOperatorVars {
  taskId: string;
  operatorId: string | null;
}

/**
 * Assign or clear an Operator on an un-routed Task —
 * `PATCH /tasks/:id/operator`. Tasks on Trips inherit the Trip Operator and
 * are intentionally rejected by the server.
 */
export function useAssignCarrierTaskOperator() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      taskId,
      operatorId,
    }: AssignCarrierTaskOperatorVars) => {
      const { data } = await api.patch<{ task: CarrierTask }>(
        `/tasks/${taskId}/operator`,
        { operatorId },
        { headers: getAuthHeaders(token) }
      );
      return data.task;
    },
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.setQueryData(["carrier", "tasks", task.id], task);
    },
  });
}

/**
 * Operator update — `PATCH /operators/:id`. The scoped server allowlist
 * includes the canonical CarrierUser name plus operational Operator fields.
 * We type only what carrier workflows touch.
 */
export interface UpdateOperatorVars {
  operatorId: string;
  body: {
    name?: string;
    status?: string;
    dateOfBirth?: string;
    dispatchCenterId?: string;
    metadata?: Record<string, unknown>;
  };
}

export function useUpdateOperator() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ operatorId, body }: UpdateOperatorVars) => {
      const { data } = await api.patch<{
        message: string;
        operator: CarrierOperator;
      }>(`/operators/${operatorId}`, body, { headers: getAuthHeaders(token) });
      return data;
    },
    onSuccess: ({ operator }) => {
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
      qc.setQueryData(["carrier", "operators", operator.id], operator);
    },
  });
}

export function useResetOperatorPassword() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (operatorId: string) => {
      const { data } = await api.post<{ message: string; mode: string }>(
        `/operators/${operatorId}/reset-password`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
  });
}

export function useDeleteCarrierOperator() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (operatorId: string) => {
      const { data } = await api.delete<{ message: string }>(
        `/operators/${operatorId}`,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "operators"] });
    },
  });
}

/**
 * Vehicle update — `PATCH /vehicles/:id`. Server allowlist:
 * type/status/make/model/year/capacity + a few more.
 */
export interface UpdateVehicleVars {
  vehicleId: string;
  body: {
    type?: string;
    status?: string;
    make?: string;
    model?: string;
    year?: number;
    capacity?: unknown;
  };
}

export function useUpdateVehicle() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ vehicleId, body }: UpdateVehicleVars) => {
      const { data } = await api.patch<{ message: string }>(
        `/vehicles/${vehicleId}`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "vehicles"] });
    },
  });
}

export interface CreateVehicleVars {
  dispatchCenterId: string;
  code: string;
  type: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  vin?: string;
}

/**
 * `POST /carrier/vehicles` — register a vehicle.
 *
 * The dispatch centre carries the scope (it belongs to a carrier), so the body
 * names a centre and never a carrier. `code` / `licensePlate` / `vin` are
 * globally unique, so a clash comes back 409 rather than a 500.
 */
export function useCreateVehicle() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateVehicleVars) => {
      const { data } = await api.post<{ data: CarrierVehicle }>(
        "/vehicles",
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "vehicles"] });
    },
  });
}

/**
 * Team membership row as returned by `GET /api/v2/carrier/team` (server #251).
 * One CarrierUser may have multiple memberships (rare in v1) — server returns
 * them as a nested `memberships` array.
 */
export interface CarrierTeamMembership {
  id: string;
  carrierId: string;
  dispatchCenterId: string | null;
  role: "CARRIER_OWNER" | "CARRIER_DISPATCHER" | "OPERATOR";
  status: "ACTIVE" | "DISABLED";
  carrier: { id: string; name: string };
  dispatchCenter: { id: string; name: string } | null;
}

export interface CarrierTeamMember {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  active: boolean;
  createdAt: string;
  memberships: CarrierTeamMembership[];
}

export interface CarrierTeamResponse {
  data: CarrierTeamMember[];
  metadata: PageMetadata;
}

/**
 * Active team members — `GET /api/v2/carrier/team` (server #251). Carrier
 * Admin only server-side; Dispatcher callers get 403 from the route guard so
 * we don't surface this query on non-admin UIs.
 */
/**
 * Disable (offboard) or re-enable a team member —
 * `PATCH /carrier/team/members/:membershipId`.
 */
export function useUpdateCarrierTeamMember() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      membershipId: string;
      status?: "ACTIVE" | "DISABLED";
      role?: "CARRIER_OWNER" | "CARRIER_DISPATCHER";
      dispatchCenterId?: string | null;
      active?: boolean;
    }) => {
      const { membershipId, ...body } = vars;
      await api.patch(`/team/members/${membershipId}`, body, {
        headers: getAuthHeaders(token),
      });
      return membershipId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "team"] });
    },
  });
}

export function useCarrierTeam(params?: {
  page?: number;
  limit?: number;
  q?: string;
  status?: "ACTIVE" | "DISABLED" | "ALL";
  active?: boolean | "all";
}) {
  const { token, isLoggedIn } = useSession();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const q = params?.q?.trim() || undefined;
  const status = params?.status ?? "ACTIVE";
  const active = params?.active ?? true;
  return useQuery({
    queryKey: [
      "carrier",
      "team",
      { page, limit, q: q ?? null, status, active },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierTeamResponse>("/team", {
        headers: getAuthHeaders(token),
        params: { page, limit, ...(q && { q }), status, active },
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Pending invite row from `GET /api/v2/carrier/team/invites`. Tokens are
 * intentionally NOT returned by the list endpoint — only the create endpoint
 * surfaces the secret half.
 */
export interface CarrierTeamInvite {
  id: string;
  email: string;
  role: "CARRIER_OWNER" | "CARRIER_DISPATCHER" | "OPERATOR";
  carrierId: string;
  dispatchCenterId: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  carrier: { id: string; name: string };
  dispatchCenter: { id: string; name: string } | null;
}

export interface CarrierTeamInvitesResponse {
  data: CarrierTeamInvite[];
  metadata: PageMetadata;
}

export function useCarrierTeamInvites(params?: {
  page?: number;
  limit?: number;
  q?: string;
  status?: "PENDING" | "ACCEPTED" | "ALL";
}) {
  const { token, isLoggedIn } = useSession();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const q = params?.q?.trim() || undefined;
  const status = params?.status ?? "ALL";
  return useQuery({
    queryKey: [
      "carrier",
      "team-invites",
      { page, limit, q: q ?? null, status },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierTeamInvitesResponse>(
        "/team/invites",
        {
          headers: getAuthHeaders(token),
          params: { page, limit, ...(q && { q }), status },
        }
      );
      return data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Create a new invite — `POST /api/v2/carrier/team/invites`. Carrier Admin
 * only.
 * `carrierId` may be omitted when the caller owns exactly one carrier; the
 * server resolves it. `dispatchCenterId` null = carrier-wide scope.
 */
export interface CreateCarrierInviteVars {
  email: string;
  role: "CARRIER_OWNER" | "CARRIER_DISPATCHER";
  carrierId?: string;
  dispatchCenterId?: string | null;
}

export function useCreateCarrierInvite() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateCarrierInviteVars) => {
      const { data } = await api.post<{ invite: CarrierTeamInvite }>(
        "/team/invites",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.invite;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "team-invites"] });
      qc.invalidateQueries({ queryKey: ["carrier", "team"] });
    },
  });
}

/**
 * Revoke a pending invite — `DELETE /api/v2/carrier/team/invites/:id`.
 * Server 409s if already accepted (membership lifecycle is a separate API).
 */
export function useDeleteCarrierInvite() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      await api.delete(`/team/invites/${inviteId}`, {
        headers: getAuthHeaders(token),
      });
      return inviteId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "team-invites"] });
    },
  });
}

/**
 * Resend a pending team invite — `POST /carrier/team/invites/:id/resend`.
 */
export function useResendCarrierInvite() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      await api.post(
        `/team/invites/${inviteId}/resend`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return inviteId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "team-invites"] });
    },
  });
}

/**
 * Accept an invite — `POST /api/v2/carrier/invites/:token/accept`. PUBLIC:
 * no Bearer token (the recipient does not yet have an EZX session). The
 * Auth0 signup happens client-side first; the resulting auth0Id is posted
 * here to materialize the CarrierUser + membership.
 */
export interface AcceptCarrierInviteVars {
  token: string;
  name: string;
  phone?: string;
  auth0Id: string;
}

/**
 * `POST /invites/:token/accept` response. `palInstallUrl` is non-null only for
 * OPERATOR invites (the admin-configured PAL install link) — owners and
 * dispatchers get a portal, operators get the driver app.
 */
export interface AcceptCarrierInviteResponse {
  carrierUserId: string;
  role: CarrierMembershipRole;
  palInstallUrl: string | null;
}

export function useAcceptCarrierInvite() {
  return useMutation({
    mutationFn: async ({
      token,
      name,
      phone,
      auth0Id,
    }: AcceptCarrierInviteVars) => {
      const { data } = await api.post<AcceptCarrierInviteResponse>(
        `/invites/${token}/accept`,
        { name, auth0Id, ...(phone ? { phone } : {}) }
      );
      return data;
    },
  });
}

/**
 * Denormalized address snapshot on a shipment — one per FROM/TO/RETURN leg.
 * Includes the linked address for records whose snapshot fields are missing.
 */
export interface CarrierShipmentAddress {
  id: string;
  type: "FROM" | "TO" | "RETURN";
  contactName: string | null;
  contactCompany: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressCity: string | null;
  addressProvince: string | null;
  addressCountry: string | null;
  addressPostalCode: string | null;
  instructions: string | null;
  address?: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    province: string | null;
    country: string | null;
    postalCode: string | null;
  } | null;
}

export interface CarrierShipmentPackage {
  id: string;
  index: number | null;
  individualTrackingNumber?: string | null;
  weightGrams: number | null;
  lengthCm?: string | number | null;
  widthCm?: string | number | null;
  heightCm?: string | number | null;
  description: string | null;
}

export interface CarrierShipmentPricing {
  id?: string;
  baseRateCents?: number | null;
  subtotalCents?: number | null;
  taxCents?: number | null;
  totalCents?: number | null;
  currency?: string | null;
}

export interface CarrierShipmentProofMedia {
  id: string;
  type: string;
  key?: string;
  url: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CarrierShipmentProof {
  id?: string;
  notes: string | null;
  createdAt?: string;
  updatedAt?: string;
  mediaItems: CarrierShipmentProofMedia[];
}

export interface CarrierShipmentTask {
  id: string;
  taskNumber?: number;
  type: string;
  status: string;
  description?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  receivedAt?: string | null;
  failureReason?: string | null;
  failureReasonCode?: number | null;
  createdAt?: string;
  updatedAt?: string;
  dispatchCenter?: {
    id: string;
    name: string;
    code: string | null;
  } | null;
  trip?: {
    id: string;
    tripNumber: number;
    status: string;
    operator?: {
      id: string;
      carrierUser: {
        name: string | null;
        email: string | null;
      } | null;
    } | null;
  } | null;
  operator?: {
    id: string;
    carrierUser: {
      name: string | null;
      email: string | null;
      phone: string | null;
    } | null;
  } | null;
  proof: CarrierShipmentProof | null;
}

/**
 * Shipment row as returned by `GET /api/v2/carrier/shipments` (server #249).
 * The server `include`s order + packages + addresses + carrier, so all of
 * these are available to the list/detail UI.
 */
export interface CarrierShipment {
  id: string;
  shipmentNumber: number;
  deliveryNotes: string | null;
  trackingNumber: string | null;
  status: string;
  createdAt: string;
  pickupBookingId: string | null;
  trackingUrl: string | null;
  serviceCode: string | null;
  shipDate: string | null;
  estimatedDelivery: string | null;
  deliveryTimeZone: string | null;
  deliveryPrecision: "DATETIME" | "DATE" | null;
  carrier: {
    id: string;
    code: string;
    name: string;
    /** See CarrierShipmentLabel.carrier.selfRated — replaces `accessType`. */
    selfRated?: boolean;
  } | null;
  order: {
    id: string;
    orderNumber: number;
    placerCustomer: {
      id: string;
      customerNumber: number;
      email: string;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
    } | null;
  } | null;
  packages: CarrierShipmentPackage[];
  addresses: CarrierShipmentAddress[];
  pricing: CarrierShipmentPricing | null;
  tasks: CarrierShipmentTask[];
}

export interface CarrierShipmentDetail extends CarrierShipment {
  updatedAt: string;
  carrierCode: string | null;
  actualDelivery: string | null;
  pickupBooking: {
    id: string;
    dispatchCenterId: string | null;
  } | null;
}

export interface CarrierShipmentsResponse {
  data: CarrierShipment[];
  metadata: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface CarrierOrderShipment {
  id: string;
  shipmentNumber: number;
  status: string;
  trackingNumber: string | null;
  trackingUrl: string | null;
  carrierTrackingNumber: string | null;
  masterTracking: string | null;
  providerRef: string | null;
  carrierId: string | null;
  carrierCode: string | null;
  finalCarrierCode: string | null;
  serviceCode: string | null;
  packageCode: string | null;
  carrierServiceId: string | null;
  pickupBookingId: string | null;
  sellAmountCents: number | null;
  pickupPriceCents: number | null;
  shipDate: string | null;
  deliveryDate: string | null;
  estimatedDelivery: string | null;
  deliveryTimeZone: string | null;
  deliveryPrecision: "DATETIME" | "DATE" | null;
  actualDelivery: string | null;
  serviceCompletedAt: string | null;
  currency: string;
  carrierService: {
    id: string;
    code: string;
    name: string;
    estimatedDays: number | null;
  } | null;
  pricing: {
    subtotalCents: number;
    taxCents: number;
    totalCents: number;
    deliveryFeeCents: number;
    pickupFeeCents: number;
    pickupBundleDiscountCents: number;
  } | null;
  pickupBooking: {
    id: string;
    dispatchCenterId: string | null;
    scheduledDate: string;
    slot: {
      id: string;
      name: string;
      startTime: string;
      endTime: string;
    } | null;
    dispatchCenter: {
      id: string;
      name: string;
      code: string | null;
    } | null;
  } | null;
  carrier: {
    id: string;
    name: string;
    code: string;
  } | null;
  packages: Array<{
    id: string;
    individualTrackingNumber: string | null;
    weightGrams: number | null;
  }>;
  addresses: Array<{
    id: string;
    type: string;
    contactName: string | null;
    contactCompany: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    addressCity: string | null;
    addressProvince: string | null;
    addressCountry: string | null;
    addressPostalCode: string | null;
    address: {
      line1: string;
      line2: string | null;
      city: string;
      province: string;
      country: string;
      postalCode: string | null;
    } | null;
  }>;
  tasks: Array<{
    id: string;
    taskNumber: number;
    type: string;
    status: string;
    dispatchCenter: {
      id: string;
      name: string;
      code: string | null;
    } | null;
    operator: {
      id: string;
      carrierUser: {
        name: string | null;
        email: string | null;
      } | null;
    } | null;
    trip: {
      id: string;
      tripNumber: number;
      status: string;
      operator: {
        id: string;
        carrierUser: {
          name: string | null;
          email: string | null;
        } | null;
      } | null;
    } | null;
  }>;
  relatedRecords: {
    shipment: { id: string; apiPath: string };
    tasks: Array<{ id: string; apiPath: string }>;
    trips: Array<{ id: string; apiPath: string }>;
  };
}

export interface CarrierOrder {
  id: string;
  orderNumber: number;
  createdAt: string;
  updatedAt: string;
  currency: string;
  subtotalCents: number | null;
  taxCents: number | null;
  totalCents: number | null;
  financialScope: "FULL_ORDER" | "PARTIAL_ORDER";
  references: {
    orderId: string;
    orderNumber: number;
    legacyOrderId: string | null;
    legacyTripId: string | null;
  };
  placerCustomerId: string;
  placerCustomer: {
    id: string;
    customerNumber: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    businessAccount: {
      companyName: string | null;
    } | null;
  } | null;
  shipments: CarrierOrderShipment[];
}

export interface CarrierOrdersResponse {
  data: CarrierOrder[];
  metadata: PageMetadata;
}

/**
 * Orders list — `GET /api/v2/carrier/orders`.
 * Server derives carrier scope and returns only carrier-visible shipments.
 */
export function useCarrierOrders(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: "PICKUP" | "DROPOFF" | "ALL";
  customerEmail?: string;
  orderId?: string;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const search = params?.search?.trim() || undefined;
  const status =
    params?.status && params.status !== "ALL" ? params.status : undefined;
  const type = params?.type && params.type !== "ALL" ? params.type : undefined;
  const customerEmail = params?.customerEmail?.trim() || undefined;
  const orderId = params?.orderId?.trim() || undefined;
  return useQuery({
    queryKey: [
      "carrier",
      "orders",
      {
        page,
        limit,
        search: search ?? null,
        status: status ?? null,
        type: type ?? null,
        customerEmail: customerEmail ?? null,
        orderId: orderId ?? null,
        dispatchCenterId,
      },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierOrdersResponse>("/orders", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam(
          {
            page,
            limit,
            ...(search && { search }),
            ...(status && { status }),
            ...(type && { type }),
            ...(customerEmail && { customerEmail }),
            ...(orderId && { orderId }),
          },
          dispatchCenterId
        ),
      });
      // Defend the list against partial payloads (e.g. an API build that
      // predates the addresses/tasks relations): coerce each shipment's
      // relation arrays so a missing field can't crash a render helper.
      return {
        ...data,
        data: (data.data ?? []).map((order) => ({
          ...order,
          currency: order.currency ?? "CAD",
          subtotalCents: order.subtotalCents ?? null,
          taxCents: order.taxCents ?? null,
          totalCents: order.totalCents ?? null,
          financialScope:
            order.financialScope === "FULL_ORDER"
              ? ("FULL_ORDER" as const)
              : ("PARTIAL_ORDER" as const),
          references: order.references ?? {
            orderId: order.id,
            orderNumber: order.orderNumber,
            legacyOrderId: null,
            legacyTripId: null,
          },
          shipments: (order.shipments ?? []).map((shipment) => ({
            ...shipment,
            currency: shipment.currency ?? order.currency ?? "CAD",
            addresses: shipment.addresses ?? [],
            packages: shipment.packages ?? [],
            tasks: shipment.tasks ?? [],
          })),
        })),
      };
    },
    enabled: isLoggedIn,
  });
}

/**
 * Shipments list — `GET /api/v2/carrier/shipments` (server #249). Scope is
 * implicit (server narrows via `shipment.carrierId in scope.carrierIds`).
 */
export interface CarrierChangeRequestInput {
  requestTypes: string[];
  rescheduleDate?: string;
  reschedulePreference?: "morning" | "afternoon" | "evening";
  addressCorrection?: string;
  addressLine2Correction?: string;
  deliveryInstructions?: string;
  contactPhone?: string;
  contactName?: string;
  contactEmail?: string;
  additionalNotes?: string;
}

/**
 * `POST /carrier/shipments/:id/change-requests` — provider proposes a change.
 * Always routes to review server-side (no auto-apply for carrier).
 */
export function useCreateCarrierShipmentChangeRequest() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      shipmentId: string;
      input: CarrierChangeRequestInput;
    }) => {
      const { data } = await api.post<{ data: { id: string; status: string } }>(
        `/shipments/${vars.shipmentId}/change-requests`,
        vars.input,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["carrier", "change-requests"] });
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
      qc.invalidateQueries({
        queryKey: ["carrier", "shipments", variables.shipmentId],
      });
    },
  });
}

export function useCarrierShipments(params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const status = params?.status;
  const search = params?.search?.trim() || undefined;
  return useQuery({
    queryKey: [
      "carrier",
      "shipments",
      {
        page,
        limit,
        status: status ?? null,
        search: search ?? null,
        dispatchCenterId,
      },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierShipmentsResponse>("/shipments", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam(
          { page, limit, ...(status && { status }), ...(search && { search }) },
          dispatchCenterId
        ),
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

export function useCarrierShipment(id: string | null | undefined) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "shipments", id],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierShipmentDetail }>(
        `/shipments/${id}`,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!id,
  });
}

export interface CarrierShipmentLabel {
  id: string;
  shipmentId: string;
  carrierId: string;
  labelFormat: "PDF" | "PNG" | "ZPL" | string;
  fileUrl: string | null;
  trackingNumber: string | null;
  voidedAt: string | null;
  deliveryInstructionsSnapshot: string | null;
  carrier: {
    /**
     * Priced from EZX's own rate tables — so there is no provider API to buy a
     * label from, and EZX PRINTED this one. That is the question this screen asks:
     * internal label vs carrier label.
     *
     * ⚠️ Replaces `accessType === "OWNED"`. That column is DELETED server-side, and
     * a dropped field does not error — it reads `undefined`, so every `=== "OWNED"`
     * test silently became false and the portal offered the wrong document.
     *
     * ⚠️ NOT "EZX's own fleet": EZX owns nothing physical, and any partner that
     * prices itself in the carrier portal is self-rated.
     */
    selfRated: boolean;
  };
}

export function useCarrierShipmentLabels(id: string | null | undefined) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "shipments", id, "labels"],
    queryFn: async () => {
      const { data } = await api.get<{
        data: CarrierShipmentLabel[];
        count: number;
      }>(`/shipments/${id}/labels`, { headers: getAuthHeaders(token) });
      return data.data;
    },
    enabled: isLoggedIn && !!id,
  });
}

export function useGenerateCarrierLabels() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      shipmentId: string;
      labelType?: "carrier" | "internal";
      replaceInternal?: boolean;
    }) => {
      const { data } = await api.post<{
        data: CarrierShipmentLabel[];
        count: number;
        skipped?: boolean;
        message?: string;
      }>(
        `/shipments/${vars.shipmentId}/generate-labels`,
        { replaceInternal: vars.replaceInternal === true },
        {
          headers: getAuthHeaders(token),
          params: vars.labelType ? { labelType: vars.labelType } : undefined,
        }
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
      qc.invalidateQueries({
        queryKey: ["carrier", "shipments", vars.shipmentId, "labels"],
      });
    },
  });
}

export async function downloadCarrierShipmentLabels(
  token: string | null,
  shipmentId: string,
  options?: { format?: string; labelType?: "carrier" | "internal" }
) {
  const { data } = await api.get<Blob>(
    `/shipments/${shipmentId}/labels/download`,
    {
      headers: getAuthHeaders(token),
      params: {
        ...(options?.format && { format: options.format }),
        ...(options?.labelType && { labelType: options.labelType }),
      },
      responseType: "blob",
    }
  );
  return data;
}

export async function downloadCarrierBatchShipmentLabels(
  token: string | null,
  shipmentIds: string[],
  labelType: "carrier" | "internal"
) {
  const response = await api.post<Blob>(
    "/shipments/labels/batch-download",
    { shipmentIds, labelType },
    {
      headers: getAuthHeaders(token),
      responseType: "blob",
    }
  );
  return {
    blob: response.data,
    labelCount: Number(response.headers["x-label-count"] ?? 0),
    pageCount: Number(response.headers["x-page-count"] ?? 0),
  };
}

export function useRequestCarrierShipmentCancellation() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { shipmentId: string; reason?: string }) => {
      const { data } = await api.post<{
        success: boolean;
        immediate?: boolean;
        pending?: boolean;
        message: string;
        tasksCancelled?: number;
      }>(
        `/shipments/${vars.shipmentId}/request-cancellation`,
        { reason: vars.reason },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
      qc.invalidateQueries({ queryKey: ["carrier", "cancellations"] });
      qc.invalidateQueries({ queryKey: ["carrier", "orders"] });
    },
  });
}

/**
 * Pickup-booking row as returned by `GET /api/v2/carrier/pickup-bookings`.
 * Mirrors the admin handler's decorated shape — `derivedState` and
 * `attachedShipmentCount` are computed server-side.
 */
export type CarrierPickupBookingState =
  | "Scheduled"
  | "InProgress"
  | "Completed"
  | "Failed"
  | "Cancelled";

export interface CarrierPickupBooking {
  id: string;
  scheduledDate: string;
  cancelledAt?: string | null;
  notes: string | null;
  derivedState: CarrierPickupBookingState;
  attachedShipmentCount: number;
  totalShipmentCount: number;
  placerCustomer: {
    id: string;
    customerNumber: number;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  pickupAddress: {
    id: string;
    line1?: string | null;
    line2?: string | null;
    province?: string | null;
    street?: string | null;
    city: string | null;
    state?: string | null;
    country: string | null;
    postalCode: string | null;
  } | null;
  carrierAddOnService: { id: string; code: string; name: string } | null;
  dispatchCenter: { id: string; code: string; name: string } | null;
  slot: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  } | null;
  shipments: Array<{
    id: string;
    shipmentNumber: number;
    trackingNumber: string | null;
    status: string;
    tasks: Array<{ id: string; status: string }>;
  }>;
}

export interface CarrierPickupBookingsResponse {
  data: CarrierPickupBooking[];
  pagination: { total: number; limit: number; offset: number };
}

export interface CarrierPickupBookingsParams {
  state?: CarrierPickupBookingState | "ALL";
  fromDate?: string;
  toDate?: string;
  limit?: number;
  offset?: number;
}

export type CarrierPickupServiceDay =
  | "MON"
  | "TUE"
  | "WED"
  | "THU"
  | "FRI"
  | "SAT"
  | "SUN";

export interface CarrierPickupSlot {
  id: string;
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  name: string;
  startTime: string;
  endTime: string;
  serviceDays: CarrierPickupServiceDay[];
  bookingLeadMinutes: number;
  defaultCapacity: number | null;
  isActive: boolean;
  sortOrder: number;
  dispatchCenter?: { id: string; code: string; name: string } | null;
  capacityOverrides?: Array<{
    id: string;
    date: string;
    maxCapacity: number;
  }>;
}

export interface CarrierPickupSlotsParams {
  dispatchCenterId?: string;
}

export interface CarrierPickupSlotInput {
  dispatchCenterId: string;
  name: string;
  startTime: string;
  endTime: string;
  serviceDays: CarrierPickupServiceDay[];
  bookingLeadMinutes?: number;
  defaultCapacity?: number | null;
  isActive?: boolean;
  sortOrder?: number;
}

/** `PATCH /carrier/pickup-bookings/:id` — edit operational notes only. */
export function useUpdateCarrierPickupBookingNotes() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; notes: string | null }) => {
      await api.patch(
        `/pickup-bookings/${vars.id}`,
        { notes: vars.notes },
        { headers: getAuthHeaders(token) }
      );
      return vars.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pickup-bookings"] });
    },
  });
}

/**
 * Pickup-bookings list — `GET /api/v2/carrier/pickup-bookings` (server #253).
 */
export function useCarrierPickupBookings(
  params: CarrierPickupBookingsParams = {}
) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: ["carrier", "pickup-bookings", params, { dispatchCenterId }],
    queryFn: async () => {
      const queryParams = {
        ...params,
        ...(params.state && params.state !== "ALL"
          ? { state: params.state }
          : {}),
      };
      if (params.state === "ALL") {
        delete queryParams.state;
      }

      const { data } = await api.get<
        CarrierPickupBookingsResponse | { data: CarrierPickupBooking[] }
      >("/pickup-bookings", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam(queryParams, dispatchCenterId),
      });
      if ("pagination" in data) {
        return data;
      }
      return {
        data: data.data,
        pagination: {
          total: data.data.length,
          limit: params.limit ?? data.data.length,
          offset: params.offset ?? 0,
        },
      };
    },
    enabled: isLoggedIn,
  });
}

export function useCarrierPickupSlots(
  serviceId: string | null | undefined,
  params: CarrierPickupSlotsParams = {}
) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pickup-slots", serviceId, params],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierPickupSlot[] }>(
        `/pickup-services/${serviceId}/slots`,
        {
          headers: getAuthHeaders(token),
          params,
        }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!serviceId,
  });
}

export function useCreateCarrierPickupSlot(serviceId: string) {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CarrierPickupSlotInput) => {
      const { data } = await api.post<{ data: CarrierPickupSlot }>(
        `/pickup-services/${serviceId}/slots`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["carrier", "pickup-slots", serviceId],
      });
    },
  });
}

export function useUpdateCarrierPickupSlot() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      slotId: string;
      serviceId: string;
      patch: Partial<CarrierPickupSlotInput>;
    }) => {
      const { data } = await api.patch<{ data: CarrierPickupSlot }>(
        `/pickup-slots/${vars.slotId}`,
        vars.patch,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["carrier", "pickup-slots", vars.serviceId],
      });
    },
  });
}

export function useDeleteCarrierPickupSlot() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { slotId: string; serviceId: string }) => {
      await api.delete(`/pickup-slots/${vars.slotId}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["carrier", "pickup-slots", vars.serviceId],
      });
    },
  });
}

export function useUpsertCarrierPickupSlotCapacityOverride() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      serviceId: string;
      slotId: string;
      date: string;
      maxCapacity: number;
    }) => {
      const { data } = await api.post<{
        data: { id: string; date: string; maxCapacity: number };
      }>(
        `/pickup-slots/${vars.slotId}/capacity-overrides`,
        { date: vars.date, maxCapacity: vars.maxCapacity },
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["carrier", "pickup-slots", vars.serviceId],
      });
    },
  });
}

export function useDeleteCarrierPickupSlotCapacityOverride() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { serviceId: string; overrideId: string }) => {
      await api.delete(`/pickup-slot-capacity-overrides/${vars.overrideId}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["carrier", "pickup-slots", vars.serviceId],
      });
    },
  });
}

export function useCancelCarrierPickupBooking() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      reason?: string;
      notes?: string | null;
    }) => {
      const { data } = await api.post(
        `/pickup-bookings/${vars.id}/cancel`,
        { reason: vars.reason, notes: vars.notes },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pickup-bookings"] });
    },
  });
}

export function useDetachCarrierPickupBookingShipment() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      bookingId: string;
      shipmentId: string;
      reason?: string;
    }) => {
      const { data } = await api.delete(
        `/pickup-bookings/${vars.bookingId}/shipments/${vars.shipmentId}`,
        {
          headers: getAuthHeaders(token),
          data: { reason: vars.reason },
        }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pickup-bookings"] });
    },
  });
}

export function useRescheduleCarrierPickupBooking() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      newScheduledDate: string;
      newSlotId: string;
      newPickupServiceId?: string;
      shipmentIds: string[];
    }) => {
      const body = {
        newScheduledDate: vars.newScheduledDate,
        newSlotId: vars.newSlotId,
        newPickupServiceId: vars.newPickupServiceId,
        shipmentIds: vars.shipmentIds,
      };
      const preview = await api.post<{ data: { previewFingerprint: string } }>(
        `/pickup-bookings/${vars.id}/reschedule/preview`,
        body,
        { headers: getAuthHeaders(token) }
      );
      if (!preview.data.data.previewFingerprint)
        throw new Error("Pickup preview is incomplete");
      const { data } = await api.post(
        `/pickup-bookings/${vars.id}/reschedule`,
        {
          ...body,
          expectedPreviewFingerprint: preview.data.data.previewFingerprint,
        },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pickup-bookings"] });
    },
  });
}

export function usePrintCarrierPickupBooking() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (vars: { id: string }) => {
      const response = await api.get<Blob>(
        `/pickup-bookings/${vars.id}/print`,
        {
          headers: getAuthHeaders(token),
          responseType: "blob",
        }
      );
      return {
        blob: response.data,
        filename: `pickup-booking-${vars.id}.pdf`,
      };
    },
  });
}

/**
 * Availability request row from `GET /api/v2/carrier/scheduling/requests`.
 * Server enriches with submission counts so the UI doesn't have to fan out.
 */
export interface CarrierAvailabilityRequest {
  id: string;
  weekStart: string;
  createdAt: string;
  message: string | null;
  dispatchCenter: { id: string; name: string };
  targetOperatorIds: string[];
  totalOperators: number;
  submittedCount: number;
  status?: string;
  operators: CarrierAvailabilityRequestOperator[];
  targetOperators: CarrierAvailabilityRequestOperator[];
}

export interface CarrierAvailabilityRequestOperator {
  id: string;
  name: string;
  targeted?: boolean;
  submitted: boolean;
  responseId: string | null;
  submittedAt: string | null;
  availability: unknown;
  notes: string | null;
}

export interface CarrierSchedulingRequestsResponse {
  data: CarrierAvailabilityRequest[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export function useCreateAvailabilityRequest() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      dispatchCenterId: string;
      weekStart: string;
      message?: string;
      operatorIds?: string[];
    }) => {
      const { data } = await api.post<{ id: string }>(
        "/scheduling/requests",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "scheduling", "requests"] });
    },
  });
}

export function useUpdateAvailabilityRequestStatus() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; status: string }) => {
      const { data } = await api.patch<{ id: string }>(
        `/scheduling/requests/${vars.requestId}/status`,
        { status: vars.status },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "scheduling", "requests"] });
    },
  });
}

export function useRemindAvailabilityRequest() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data } = await api.post<{ remindedCount: number }>(
        `/scheduling/requests/${requestId}/remind`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
  });
}

export function useCarrierSchedulingRequests(params?: {
  dispatchCenterId?: string;
  operatorId?: string;
  q?: string;
  status?: string;
  weekStart?: string;
  limit?: number;
  offset?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const queryParams = {
    dispatchCenterId: params?.dispatchCenterId || undefined,
    operatorId: params?.operatorId || undefined,
    q: params?.q || undefined,
    status:
      params?.status && params.status !== "ALL" ? params.status : undefined,
    weekStart: params?.weekStart || undefined,
    limit: params?.limit,
    offset: params?.offset,
  };
  return useQuery({
    queryKey: [
      "carrier",
      "scheduling",
      "requests",
      queryParams,
      { dispatchCenterId },
    ],
    queryFn: async () => {
      const { data } = await api.get<CarrierSchedulingRequestsResponse>(
        "/scheduling/requests",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(queryParams, dispatchCenterId),
        }
      );
      return data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Per-operator row in `GET /api/v2/carrier/scheduling/availability`. Server
 * requires dispatchCenterId + weekStart query params — the hook skips the fetch
 * when either is missing.
 */
export interface CarrierAvailabilityRow {
  operatorId: string;
  name: string;
  availability: unknown;
  notes: string | null;
  submittedAt: string | null;
}

export function useCarrierAvailability(
  dispatchCenterId: string | null,
  weekStart: string | null
) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId: scopedDispatchCenterId } =
    useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: [
      "carrier",
      "scheduling",
      "availability",
      dispatchCenterId,
      weekStart,
      { dispatchCenterId: scopedDispatchCenterId },
    ],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierAvailabilityRow[] }>(
        "/scheduling/availability",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(
            { dispatchCenterId, weekStart },
            scopedDispatchCenterId
          ),
        }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!dispatchCenterId && !!weekStart,
  });
}

/**
 * Per-operator row in `GET /api/v2/carrier/scheduling/shifts`. Each row's
 * `shifts` array contains the operator's scheduled shifts for the week.
 */
export interface CarrierShiftRow {
  operatorId: string;
  name: string;
  shifts: Array<{
    id: string;
    date: string;
    shiftType: string;
    startTime: string | null;
    endTime: string | null;
    status: string;
    notes: string | null;
  }>;
}

export function useCarrierShifts(
  dispatchCenterId: string | null,
  weekStart: string | null
) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId: scopedDispatchCenterId } =
    useOptionalDispatchCenterScope();
  return useQuery({
    queryKey: [
      "carrier",
      "scheduling",
      "shifts",
      dispatchCenterId,
      weekStart,
      { dispatchCenterId: scopedDispatchCenterId },
    ],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierShiftRow[] }>(
        "/scheduling/shifts",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam(
            { dispatchCenterId, weekStart },
            scopedDispatchCenterId
          ),
        }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!dispatchCenterId && !!weekStart,
  });
}

export function useCreateCarrierShift() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      operatorId: string;
      dispatchCenterId: string;
      date: string;
      shiftType: string;
      startTime?: string;
      endTime?: string;
      notes?: string;
    }) => {
      const { data } = await api.post<{ id: string }>(
        "/scheduling/shifts",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "scheduling", "shifts"] });
    },
  });
}

export function useUpdateCarrierShift() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      shiftId: string;
      data: {
        status?: string;
        startTime?: string;
        endTime?: string;
        notes?: string;
      };
    }) => {
      const { data } = await api.patch<{ id: string }>(
        `/scheduling/shifts/${vars.shiftId}`,
        vars.data,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "scheduling", "shifts"] });
    },
  });
}

export function useDeleteCarrierShift() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (shiftId: string) => {
      await api.delete(`/scheduling/shifts/${shiftId}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "scheduling", "shifts"] });
    },
  });
}

/**
 * Cancellation row — `GET /api/v2/carrier/cancellations` (server #255).
 * Mirrors the admin pending-cancellations shape. Only the fields the
 * carrier-web table reads; the server include is wider.
 */
export interface CarrierCancellation {
  id: string;
  shipmentNumber: number;
  trackingNumber: string | null;
  status: string;
  cancellationRequestedAt: string | null;
  cancellationRequestReason?: string | null;
  cancellationReason: string | null;
  order: {
    id: string;
    orderNumber: number;
    placerCustomer: {
      firstName: string | null;
      lastName: string | null;
      email: string;
    } | null;
    payments?: Array<{
      id: string;
      amountCents: number;
      method: string;
    }>;
  } | null;
  tasks?: Array<{
    id: string;
    type: string;
    status: string;
    tripId: string | null;
    waypointId: string | null;
    dispatchCenterId: string | null;
    carrierId: string | null;
  }>;
  custody?:
    | { requiresDisposition: false; custody: { kind: "NONE" } }
    | {
        requiresDisposition: true;
        custody:
          | {
              kind: "COMPLETED_TASK";
              carrierId: string | null;
              dispatchCenterId: string | null;
            }
          | {
              kind: "SHIPMENT_STATUS";
              carrierId: string | null;
            };
      };
  refundInfo: {
    refundAmountCents?: number;
    totalRefund?: number;
  } | null;
  pendingProviderRefundLine?: {
    id: string;
    refundRequestId: string;
    chargeType: string;
    amountNetCents: number;
    taxCents: number;
    amountGrossCents: number;
    status: string;
    createdAt: string;
    source: string;
    reason: string | null;
  } | null;
}

export interface CarrierCancellationsResponse {
  data: CarrierCancellation[];
  pagination: OffsetPagination;
  count?: number;
}

export function useCarrierCancellations(params?: {
  limit?: number;
  offset?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  return useQuery({
    queryKey: ["carrier", "cancellations", { limit, offset, dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<CarrierCancellationsResponse>(
        "/cancellations",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam({ limit, offset }, dispatchCenterId),
        }
      );
      return data;
    },
    enabled: isLoggedIn,
  });
}

export interface CarrierCancellationReturnOption {
  returnTransportId: string;
  requestFingerprint: string;
  quoteId?: string;
  carrier: { id: string; code: string; name: string };
  carrierService: { id: string; code: string; name: string };
  labelPriceCents?: number;
  taxCents: number;
  returnChargeCents: number;
  currency: "CAD";
  expiresAt?: string;
}

export type ProcessCarrierCancellationInput = {
  shipmentId: string;
  action: "resume" | "approve";
  reason: string;
  disposition?:
    | {
        type: "HOLD_AT_DEPOT";
        dispatchCenterId: string;
        externalInterceptReference?: string;
      }
    | {
        type: "RETURN_TO_SENDER";
        returnTransportId: string;
        expectedRequestFingerprint: string;
        payer: "CUSTOMER" | "CARRIER";
        externalInterceptReference?: string;
      };
};

export function useProcessCarrierCancellation() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shipmentId,
      ...body
    }: ProcessCarrierCancellationInput) => {
      const { data } = await api.post<{ success: boolean; message: string }>(
        `/shipments/${shipmentId}/process-cancellation`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSettled: async (_data, error) => {
      if (!error || hasHttpStatus(error, 409)) {
        await Promise.all([
          qc.invalidateQueries({ queryKey: ["carrier", "cancellations"] }),
          qc.invalidateQueries({ queryKey: ["carrier", "tasks"] }),
          qc.invalidateQueries({ queryKey: ["carrier", "trips"] }),
        ]);
      }
    },
  });
}

export function useCarrierCancellationReturnOptions() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (shipmentId: string) => {
      const { data } = await api.post<{
        kind: string;
        data?: { options: CarrierCancellationReturnOption[]; reason?: string };
      }>(
        `/shipments/${shipmentId}/cancellation-return-options`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
  });
}

/**
 * Change-request row — `GET /api/v2/carrier/change-requests` (server #255).
 * Server flattens submittedBy + shipment number into top-level fields.
 */
export interface CarrierChangeRequest {
  id: string;
  shipmentId: string;
  trackingNumber: string | null;
  shipmentNumber: number;
  submittedBy: {
    type: "CUSTOMER" | "CARRIER" | "ADMIN";
    id: string;
    name: string;
    email: string | null;
  };
  status: string;
  requestTypes: string[];
  rescheduleDate: string | null;
  reschedulePreference: string | null;
  addressCorrection: string | null;
  addressLine2Correction: string | null;
  deliveryInstructions: string | null;
  contactPhone: string | null;
  contactName: string | null;
  contactEmail: string | null;
  additionalNotes: string | null;
  adminResponse: string | null;
  appliedChanges: string[];
  handledAt: string | null;
  handledBy: string | null;
  createdAt: string;
}

export function useCarrierChangeRequests(params?: {
  limit?: number;
  offset?: number;
  status?: string;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const status = params?.status?.trim() || undefined;
  return useQuery({
    queryKey: [
      "carrier",
      "change-requests",
      { limit, offset, status: status ?? null, dispatchCenterId },
    ],
    queryFn: async () => {
      const { data } = await api.get<{
        requests: CarrierChangeRequest[];
        pagination: { total: number; limit: number; offset: number };
      }>("/change-requests", {
        headers: getAuthHeaders(token),
        params: withDispatchCenterParam(
          { limit, offset, ...(status && { status }) },
          dispatchCenterId
        ),
      });
      return data;
    },
    enabled: isLoggedIn,
  });
}

export function useHandleCarrierChangeRequest() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      status: "ACTIONED" | "CANNOT_FULFILL";
      adminResponse?: string;
    }) => {
      const { data } = await api.patch<{
        data: {
          id: string;
          status: string;
          adminResponse: string | null;
          handledAt: string | null;
          appliedChanges: string[];
        };
      }>(
        `/change-requests/${vars.id}`,
        {
          status: vars.status,
          ...(vars.adminResponse && { adminResponse: vars.adminResponse }),
        },
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "change-requests"] });
      qc.invalidateQueries({ queryKey: ["carrier", "shipments"] });
      qc.invalidateQueries({ queryKey: ["carrier", "tasks"] });
      qc.invalidateQueries({ queryKey: ["carrier", "trips"] });
    },
  });
}

/**
 * Provider-ownership refunds: a carrier's pending refund LINES — the lines for
 * services it provides that it can approve to release the customer's refund.
 */
export interface CarrierRefundLine {
  id: string;
  chargeType: string;
  amountNetCents: number;
  taxCents: number;
  amountGrossCents: number;
  createdAt: string;
  refundRequest: {
    id: string;
    source: string;
    reason: string | null;
    requestedAt: string;
    requestedByCustomer: {
      id: string;
      customerNumber: number;
      email: string;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
    } | null;
    requestedByInternalUser: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
    } | null;
  };
  shipment: {
    id: string;
    shipmentNumber: number;
    order: {
      placerCustomer: {
        id: string;
        customerNumber: number;
        email: string;
        firstName: string | null;
        lastName: string | null;
        phone: string | null;
      } | null;
    } | null;
  } | null;
}

export interface CarrierRefundLinesResponse {
  data: CarrierRefundLine[];
  pagination: OffsetPagination;
}

export function useCarrierRefundLines(params?: {
  limit?: number;
  offset?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const { dispatchCenterId } = useOptionalDispatchCenterScope();
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  return useQuery({
    queryKey: ["carrier", "refund-lines", { limit, offset, dispatchCenterId }],
    queryFn: async () => {
      const { data } = await api.get<CarrierRefundLinesResponse>(
        "/refund-lines",
        {
          headers: getAuthHeaders(token),
          params: withDispatchCenterParam({ limit, offset }, dispatchCenterId),
        }
      );
      return data;
    },
    enabled: isLoggedIn,
  });
}

export function useApproveCarrierRefundLine() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { refundRequestId: string; lineId: string }) => {
      const { data } = await api.post(
        `/refund-requests/${input.refundRequestId}/lines/${input.lineId}/approve`,
        {},
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "refund-lines"] });
      qc.invalidateQueries({ queryKey: ["carrier", "cancellations"] });
      qc.invalidateQueries({ queryKey: ["carrier", "refund-action-events"] });
    },
  });
}

export function useDisputeCarrierRefundLine() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      refundRequestId: string;
      lineId: string;
      reason: string;
    }) => {
      const { data } = await api.post(
        `/refund-requests/${input.refundRequestId}/lines/${input.lineId}/dispute`,
        { reason: input.reason },
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "refund-lines"] });
      qc.invalidateQueries({ queryKey: ["carrier", "cancellations"] });
      qc.invalidateQueries({ queryKey: ["carrier", "refund-action-events"] });
    },
  });
}

export interface CarrierRefundActionEvent {
  id: string;
  type: "ASSIGNED" | "REMINDER" | "RESOLVED" | string;
  actionNeeded: boolean;
  resolvedAt: string | null;
  reason: string | null;
  details: Record<string, unknown> | null;
  createdAt: string;
  actorCarrier: { id: string; email: string; name: string | null } | null;
  actorInternal: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  refundRequest: {
    id: string;
    source: string;
    status: string;
    reason: string | null;
    requestedAt: string;
    orderId: string | null;
  };
  refundLine: {
    id: string;
    shipmentId: string | null;
    chargeType: string;
    amountGrossCents: number;
    approvedGrossCents: number | null;
    status: string;
    approverType: string;
    providerCarrierId: string | null;
  } | null;
}

export function useCarrierRefundActionEvents(params: {
  actionNeeded: boolean;
  limit?: number;
}) {
  const { token, isLoggedIn } = useSession();
  const limit = params.limit ?? 100;
  return useQuery({
    queryKey: [
      "carrier",
      "refund-action-events",
      { actionNeeded: params.actionNeeded, limit },
    ],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierRefundActionEvent[] }>(
        "/refund-action-events",
        {
          headers: getAuthHeaders(token),
          params: { actionNeeded: String(params.actionNeeded), limit },
        }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Carrier pricing self-serve. Carrier Admin only (server middleware enforces);
 * the UI just renders 403s as "not authorized".
 */

export interface CarrierPricingOverview {
  transportServices: number;
  addOnServices: number;
  costRateBands: number;
  surcharges: number;
  discounts: number;
}

/**
 * Pricing overview — `GET /api/v2/carrier/pricing`. Counts only; cheap.
 */
export function useCarrierPricingOverview() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "overview"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierPricingOverview }>(
        "/pricing",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * The rate card, typed per entity.
 *
 * This used to be `PricingRow = Record<string, unknown>`, which is why the
 * workspace rendered `fromServiceAreaId=cmj0pnigb…` key=value dumps: the UI
 * genuinely did not know what fields a row had, so it iterated the object and
 * printed whatever fell out (skipping the nested relations — i.e. exactly the
 * resolved names the server was already sending).
 *
 * These mirror the server's `include` shapes in get-cost-rates.handler.ts.
 * Prisma Decimals arrive over JSON as strings; keep them as `string | number`
 * rather than pretending they're numbers.
 */
export type Decimalish = string | number;

/** A named service-area endpoint. */
export interface ServiceAreaRef {
  id: string;
  name: string;
  code: string;
}

export interface CarrierServiceRef {
  id: string;
  code: string;
  name: string;
  carrierId: string;
}

export interface AddOnServiceRef {
  id: string;
  code: string;
  name: string;
  providerCarrierId: string;
}

export interface DispatchCenterRef {
  id: string;
  name: string;
}

/** Label cost band — the carrier's own, and editable. */
export interface CarrierRateBandRow {
  id: string;
  carrierServiceId: string;
  weightFromKg: Decimalish;
  weightToKg: Decimalish;
  baseRateCents: number;
  perKgCents: number | null;
  anchorWeightKg: Decimalish | null;
  rushHourSurchargeCents: number | null;
  currencyCode: string;
  carrierService: CarrierServiceRef | null;
  fromServiceArea: ServiceAreaRef;
  toServiceArea: ServiceAreaRef;
}

/** Pickup leg: sender → dispatch centre (read-only for carriers). */
export interface PickupRateBandRow {
  id: string;
  fromServiceAreaId: string;
  dispatchCenterId: string;
  weightFromKg: Decimalish;
  weightToKg: Decimalish;
  baseRateCents: number;
  perKgCents: number | null;
  anchorWeightKg?: Decimalish | null;
  rushHourSurchargeCents?: number | null;
  currencyCode: string;
  carrierAddOnService: AddOnServiceRef;
  fromServiceArea: ServiceAreaRef;
  dispatchCenter: DispatchCenterRef;
}

/** Redistribution leg: dispatch centre → carrier intake (read-only). */
export interface RedistributionRateBandRow {
  id: string;
  dispatchCenterId: string;
  carrierId: string;
  weightFromKg: Decimalish;
  weightToKg: Decimalish;
  baseRateCents: number;
  perKgCents: number | null;
  anchorWeightKg?: Decimalish | null;
  rushHourSurchargeCents?: number | null;
  currencyCode: string;
  dispatchCenter: DispatchCenterRef;
}

/** Rows the read-only tables still render generically. */
export type PricingRow = Record<string, unknown> & { id: string };

/**
 * A label service in the carrier workspace.
 *
 * `selfRated` is true when the carrier has an active first-party supply
 * channel. API-procured services receive their label price live and therefore
 * have no editable label rate bands.
 */
export type CarrierTransportServiceRow = PricingRow & {
  carrierId: string;
  code: string;
  name: string;
  estimatedDays: number | null;
  isActive: boolean;
  dimFactorCm: Decimalish | null;
  dimFactorInch: Decimalish | null;
  maxWeightKg: Decimalish | null;
  maxLongestSideCm: Decimalish | null;
  maxLengthPlusGirthCm: Decimalish | null;
  sourceProviderIds: string[];
  firstSeenAt: string;
  lastSeenAt: string;
  createdAt: string;
  updatedAt: string;
  selfRated: boolean;
  carrier: {
    id: string;
    name: string | null;
  };
};

export interface CarrierCostRatesPayload {
  labelBands: CarrierRateBandRow[];
  pickupBands: PickupRateBandRow[];
  redistributionBands: RedistributionRateBandRow[];
}

/**
 * Cost rates — `GET /api/v2/carrier/pricing/cost-rates`. Returns grouped
 * arrays so the UI can show one table per leg.
 */
export function useCarrierCostRates() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "cost-rates"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierCostRatesPayload }>(
        "/pricing/cost-rates",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * Shipping-service catalog. The endpoint keeps its legacy
 * `GET /api/v2/carrier/pricing/transport-services` path for compatibility.
 * CarrierService rows.
 */
export function useCarrierTransportServices() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "transport-services"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierTransportServiceRow[] }>(
        "/pricing/transport-services",
        {
          headers: getAuthHeaders(token),
          params: { includeInactive: true },
        }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

export interface CreateCarrierServiceVars {
  /** lowercase + underscore, e.g. `saturday_delivery`. Immutable once set. */
  code: string;
  name: string;
  estimatedDays?: number | null;
}

/**
 * `POST /carrier/services` — the carrier writes its own listing.
 *
 * No carrierId in the body: the carrier surface has no scope picker, so the
 * server takes it from the actor. Refused 422 `NO_PLATFORM_FEE` when EZX has
 * set no fee for us — the service could never be quoted, so it is better to
 * fail here than at a customer's checkout.
 */
export function useCreateCarrierService() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCarrierServiceVars) => {
      const { data } = await api.post<{ data: PricingRow }>("/services", body, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pricing"] });
    },
  });
}

export interface UpdateCarrierServiceVars {
  serviceId: string;
  patch: {
    name?: string;
    estimatedDays?: number | null;
    isActive?: boolean;
    /**
     * Volumetric divisors and package-fit ceilings. The Server has accepted
     * these on this route since #265; nothing in the Console sent them, so a
     * carrier could set them once at creation and never again.
     *
     * `null` clears a ceiling, omitted leaves it untouched — the distinction
     * matters: an omitted ceiling is "unchanged", a null one is "no limit".
     */
    dimFactorCm?: number | null;
    dimFactorInch?: number | null;
    maxWeightKg?: number | null;
    maxLongestSideCm?: number | null;
    maxLengthPlusGirthCm?: number | null;
  };
}

/**
 * `PATCH /carrier/services/:id` — rename, retime, or retire a service.
 *
 * Retiring is `isActive: false`, not DELETE (which stays EZX-only): it stops
 * the service being quoted while leaving its shipments and rate bands intact.
 */
export function useUpdateCarrierService() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ serviceId, patch }: UpdateCarrierServiceVars) => {
      const { data } = await api.patch<{ data: PricingRow }>(
        `/services/${serviceId}`,
        patch,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "pricing"] });
    },
  });
}

/**
 * Add-on services for pricing — `GET /api/v2/carrier/pricing/add-on-services`.
 * Named `…ForPricing` to disambiguate from the platform add-on catalog screens
 * under `/add-on-services/*`.
 */
export function useCarrierAddOnServicesForPricing() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "add-on-services"],
    queryFn: async () => {
      const { data } = await api.get<{ data: PricingRow[] }>(
        "/pricing/add-on-services",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

/**
 * The EZX fee that actually applies to one of your services, already resolved
 * by the server (per-service override, else the carrier-wide default).
 *
 * EZX's, not yours — read-only. But disclosed: you can back it out of any
 * customer quote anyway, and once payouts exist it is the second line of your
 * statement. `percent`/`flatCents` are null when NO fee is configured at either
 * level, which means this service cannot be quoted at all.
 */
export interface PlatformFeeRow {
  carrierServiceId: string;
  code: string;
  name: string;
  percent: number | null;
  flatCents: number | null;
  source: "service" | "carrier" | null;
}

export interface CarrierSurchargesPayload {
  platformFees: PlatformFeeRow[];
  propertyTypeSurcharges: PricingRow[];
}

/**
 * Surcharges — `GET /api/v2/carrier/pricing/surcharges`. Two grouped arrays:
 * service-level markups + property-type surcharges. Carrier-wide
 * `CarrierMarkup` is intentionally NOT exposed here (platform-managed).
 */
export function useCarrierSurcharges() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "surcharges"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierSurchargesPayload }>(
        "/pricing/surcharges",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

export interface CarrierPickupBundleDiscountTier extends PricingRow {
  carrierId: string;
  carrierCustomerTierId: string | null;
  customerId: string | null;
  position: number;
  fractionOff: number;
  label: string | null;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface CarrierDiscountsPayload {
  pickupBundleDiscounts: CarrierPickupBundleDiscountTier[];
}

export interface CarrierPickupQuotePreviewInput {
  carrierServiceId: string;
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  destinationCarrierId: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  actualWeightKg: number;
  lengthCm: number;
  widthCm: number;
  heightCm: number;
}

export interface CarrierPickupQuotePreview {
  carrierServiceExists: boolean;
  labelBillableKg: number;
  pickupBillableKg: number;
  labelLegCents: number | null;
  pickupLegCents: number | null;
  redistLegCents: number | null;
  totalPickupCents: number | null;
  labelMissing: boolean;
  pickupError: { code: string; message: string } | null;
}

export interface UpdateCarrierPickupBundleDiscountInput {
  carrierId?: string;
  // Scope: omit both for the carrier-default curve; set one for a tier or
  // customer-override curve.
  carrierCustomerTierId?: string | null;
  customerId?: string | null;
  tiers: Array<{
    position: number;
    fractionOff: number;
    label?: string | null;
  }>;
}

export type CarrierDiscountFunding = "PLATFORM" | "CARRIER";

export type CarrierDiscountRuleStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";

export interface CarrierLabelDiscountEligibility {
  id: string;
  customerId: string | null;
  carrierCustomerTierId: string | null;
  carrierServiceId: string | null;
  dispatchCenterId: string | null;
}

export interface CarrierLabelDiscountRule {
  funding: CarrierDiscountFunding;
  id: string;
  carrierId: string;
  name: string;
  status: CarrierDiscountRuleStatus;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
  ruleId: string;
  chargeScope: "LABEL";
  discountType: "PERCENTAGE";
  percentage: number;
  eligibilityRows: CarrierLabelDiscountEligibility[];
}

export interface CarrierLabelDiscountRuleInput {
  funding?: CarrierDiscountFunding;
  carrierId?: string;
  name?: string;
  status?: CarrierDiscountRuleStatus;
  startsAt?: string | null;
  endsAt?: string | null;
  percentage?: number;
  eligibilityRows?: Array<{
    customerId?: string | null;
    carrierCustomerTierId?: string | null;
    carrierServiceId?: string | null;
    dispatchCenterId?: string | null;
  }>;
}

/**
 * Discounts — `GET /api/v2/carrier/pricing/discounts`.
 */
export function useCarrierDiscounts() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "pricing", "discounts"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierDiscountsPayload }>(
        "/pricing/discounts",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

export function usePreviewCarrierPickupQuote() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (input: CarrierPickupQuotePreviewInput) => {
      const { data } = await api.post<{
        success: boolean;
        data: CarrierPickupQuotePreview;
      }>("/pricing/preview-pickup-quote", input, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
  });
}

export function useUpdateCarrierPickupBundleDiscount() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateCarrierPickupBundleDiscountInput) => {
      const { data } = await api.put<{
        success: boolean;
        data: CarrierPickupBundleDiscountTier[];
      }>("/pricing/discounts/pickup-bundle", input, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["carrier", "pricing", "discounts"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["carrier", "pricing", "overview"],
      });
    },
  });
}

export function useCarrierLabelDiscountRules() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "label-discount-rules"],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierLabelDiscountRule[] }>(
        "/discount-rules",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

export function useCreateCarrierLabelDiscountRule() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CarrierLabelDiscountRuleInput) => {
      const { data } = await api.post<{ data: CarrierLabelDiscountRule }>(
        "/discount-rules",
        input,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["carrier", "label-discount-rules"],
      });
    },
  });
}

export function useUpdateCarrierLabelDiscountRule() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: CarrierLabelDiscountRuleInput & { id: string }) => {
      const { data } = await api.patch<{ data: CarrierLabelDiscountRule }>(
        `/discount-rules/${id}`,
        input,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["carrier", "label-discount-rules"],
      });
    },
  });
}

export function useDeleteCarrierLabelDiscountRule() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/discount-rules/${id}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["carrier", "label-discount-rules"],
      });
    },
  });
}

/**
 * Rate-band writes. The carrier edits its own rate card directly — EZX is a
 * marketplace, the carrier supplies its own pricing. There is no approval
 * queue (the old propose→approve→apply flow never applied a single change and
 * was deleted); EZX's controls are the take rate, the server-side validation
 * guardrails, and the go-live switch on the carrier itself.
 *
 * Server: /api/v2/carrier/pricing/rate-bands — the same handlers the admin
 * console uses, scoped to this carrier by PricingPolicy.
 */
export interface RateBandWriteVars {
  carrierServiceId: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
}

const invalidateRates = (qc: ReturnType<typeof useQueryClient>) => {
  void qc.invalidateQueries({ queryKey: ["carrier", "pricing", "cost-rates"] });
  void qc.invalidateQueries({ queryKey: ["carrier", "pricing", "overview"] });
};

export function useCreateCarrierRateBand() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RateBandWriteVars) => {
      const { data } = await api.post<{ data: CarrierRateBandRow }>(
        "/pricing/rate-bands",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => invalidateRates(qc),
  });
}

export function useUpdateCarrierRateBand() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...vars
    }: Partial<RateBandWriteVars> & { id: string }) => {
      const { data } = await api.patch<{ data: CarrierRateBandRow }>(
        `/pricing/rate-bands/${id}`,
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => invalidateRates(qc),
  });
}

export function useDeleteCarrierRateBand() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/pricing/rate-bands/${id}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: () => invalidateRates(qc),
  });
}

// ─── Customer carrier discount management ──────────────────────────────────

export interface CarrierCustomerTier {
  id: string;
  carrierId: string;
  name: string;
  normalizedName?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: { connections: number };
}

export type CarrierCustomerConnectionStatus = "ACTIVE" | "BLOCKED";

export interface CarrierCustomerConnection {
  id: string;
  customerId: string;
  carrierId: string;
  status: CarrierCustomerConnectionStatus;
  blockedReason: string | null;
  blockedAt: string | null;
  customerRemovedAt: string | null;
  addedAt: string;
  tierId: string | null;
  customer: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  tier: CarrierCustomerTier | null;
  removed: boolean;
  orderCount: number;
  effectiveDiscountPercentage: number | null;
}

export interface CarrierCustomersResponse {
  data: CarrierCustomerConnection[];
  pagination: OffsetPagination;
}

export interface PatchCarrierCustomerVars {
  customerId: string;
  tierId?: string | null;
  block?: {
    blocked: boolean;
    reason?: string | null;
  };
}

export interface CarrierCustomerTierInput {
  name: string;
  sortOrder?: number;
}

export function useCarrierCustomers(params?: {
  carrierId?: string;
  limit?: number;
  offset?: number;
  q?: string;
  status?: "ACTIVE" | "BLOCKED" | "REMOVED" | "ALL";
  /** Load every server page for complete client-side tier grouping. */
  all?: boolean;
}) {
  const { token, isLoggedIn } = useSession();
  const carrierId = params?.carrierId;
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const q = params?.q?.trim() || undefined;
  const status = params?.status ?? "ALL";
  const all = params?.all ?? false;
  return useQuery({
    queryKey: [
      "carrier",
      "customers",
      {
        carrierId: carrierId || null,
        limit,
        offset,
        q: q ?? null,
        status,
        all,
      },
    ],
    queryFn: async () => {
      const fetchPage = async (pageOffset: number, pageLimit: number) => {
        const { data } = await api.get<CarrierCustomersResponse>("/customers", {
          headers: getAuthHeaders(token),
          params: {
            ...(carrierId && { carrierId }),
            limit: pageLimit,
            offset: pageOffset,
            ...(q && { q }),
            status,
          },
        });
        return data;
      };

      if (!all) return fetchPage(offset, limit);

      return fetchAllOffsetPages(fetchPage);
    },
    enabled: isLoggedIn && carrierId !== "",
  });
}

export function useCarrierCustomerTiers(carrierId?: string) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "customer-tiers", { carrierId: carrierId || null }],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierCustomerTier[] }>(
        "/tiers",
        {
          headers: getAuthHeaders(token),
          params: { ...(carrierId && { carrierId }) },
        }
      );
      return data.data;
    },
    enabled: isLoggedIn && carrierId !== "",
  });
}

export function usePatchCarrierCustomer() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, ...body }: PatchCarrierCustomerVars) => {
      const { data } = await api.patch<{ ok: true }>(
        `/customers/${customerId}`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "customers"] });
      qc.invalidateQueries({ queryKey: ["carrier", "customer-tiers"] });
    },
  });
}

export function useCreateCarrierCustomerTier() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CarrierCustomerTierInput) => {
      const { data } = await api.post<{ data: CarrierCustomerTier }>(
        "/tiers",
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "customer-tiers"] });
    },
  });
}

export function useUpdateCarrierCustomerTier() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      tierId,
      ...vars
    }: CarrierCustomerTierInput & { tierId: string }) => {
      const { data } = await api.patch<{ data: CarrierCustomerTier }>(
        `/tiers/${tierId}`,
        vars,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "customer-tiers"] });
      qc.invalidateQueries({ queryKey: ["carrier", "customers"] });
    },
  });
}

export function useDeleteCarrierCustomerTier() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tierId: string) => {
      await api.delete(`/tiers/${tierId}`, {
        headers: getAuthHeaders(token),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "customer-tiers"] });
      qc.invalidateQueries({ queryKey: ["carrier", "customers"] });
    },
  });
}

// ─── Operational config (provider self-serve) ────────────────────────────────
// Delivery-estimates inputs: run schedules + processing time on the carrier's
// dispatch centers, and default dropoff locations per label carrier served.
// Reads: any membership. Writes: Carrier Admin only (server-enforced).

/** One internal run (mixed-task trip). departs = load deadline; returns = trip
 *  back at dispatch (>24:00 notation allowed for overnight). */
export interface DispatchRun {
  /** Stable key — becomes Trip.scheduleKey in the future planned-trips system. */
  id: string;
  departs: string;
  returns: string;
}

export type RunScheduleWeekday =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun";

export interface DispatchRunSchedule {
  weekly: Record<RunScheduleWeekday, DispatchRun[]>;
  /** Date-keyed ("YYYY-MM-DD") full replacement; [] = no runs (holiday). */
  overrides: Record<string, DispatchRun[]>;
}

export interface CarrierDispatchCenter {
  id: string;
  code: string;
  name: string;
  timeZone: string;
  processingMinutes: number;
  runSchedule: DispatchRunSchedule | null;
  isActive: boolean;
  pickupProviderCarrierId: string;
  serviceAreaId: string;
  postalCodes: string[];
  canHandlePickup: boolean;
  canHandleDelivery: boolean;
  canHandleReturn: boolean;
  canHandleTransfer: boolean;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isDefault: boolean;
  serviceCodes: string[];
  address: {
    id: string;
    line1: string;
    line2: string | null;
    city: string;
    province: string;
    country: string;
    postalCode: string | null;
    location: unknown;
    plusCode: string | null;
  } | null;
  serviceArea: { id: string; code: string; name: string } | null;
}

export function useCarrierDispatchCenters() {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier", "dispatch-centers"],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierDispatchCenter[] }>(
        "/dispatch-centers",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
  });
}

export interface CarrierDispatchCenterInput {
  carrierId?: string;
  code?: string;
  name?: string;
  address?: {
    line1?: string;
    line2?: string | null;
    city?: string;
    province?: string;
    country?: string;
    postalCode?: string | null;
    location?: unknown;
    plusCode?: string | null;
  };
  serviceAreaId?: string;
  postalCodes?: string[];
  operatingHours?: Record<string, unknown> | null;
  isActive?: boolean;
  canHandlePickup?: boolean;
  canHandleDelivery?: boolean;
  canHandleReturn?: boolean;
  canHandleTransfer?: boolean;
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isDefault?: boolean;
  serviceCodes?: string[];
  timeZone?: string;
  processingMinutes?: number;
  runSchedule?: DispatchRunSchedule | null;
}

interface UpdateDispatchCenterVars {
  dispatchCenterId: string;
  body: CarrierDispatchCenterInput;
}

export function useCreateCarrierDispatchCenter() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CarrierDispatchCenterInput) => {
      const { data } = await api.post<{ data: CarrierDispatchCenter }>(
        "/dispatch-centers",
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "dispatch-centers"] });
    },
  });
}

export function useUpdateCarrierDispatchCenter() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dispatchCenterId,
      body,
    }: UpdateDispatchCenterVars) => {
      const { data } = await api.patch<{ data: CarrierDispatchCenter }>(
        `/dispatch-centers/${dispatchCenterId}`,
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "dispatch-centers"] });
    },
  });
}

export function useDeactivateCarrierDispatchCenter() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dispatchCenterId: string) => {
      const { data } = await api.delete<{ data: CarrierDispatchCenter }>(
        `/dispatch-centers/${dispatchCenterId}`,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "dispatch-centers"] });
    },
  });
}

/** One serving cell: a label carrier I serve, configured for a specific
 *  dispatch center I operate. Default dropoff is keyed per (dispatch, label) —
 *  the package lands at that dispatch center, so the dropoff lives there. */
export interface ServingCell {
  dispatchCenterId: string;
  dispatchCenterCode: string;
  dispatchCenterName: string;
  labelCarrier: {
    id: string;
    code: string;
    name: string;
    logoUrl: string | null;
  };
  dropoffLocations: Array<{
    id: string;
    name: string;
    operatingHours: unknown;
    address: {
      line1: string;
      city: string | null;
      province: string | null;
    } | null;
  }>;
  defaultDropoffLocationId: string | null;
}

export function useCarrierServing() {
  const { token } = useSession();
  return useQuery({
    queryKey: ["carrier", "serving"],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await api.get<{ data: ServingCell[] }>("/serving", {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
  });
}

interface UpdateServingDefaultVars {
  dispatchCenterId: string;
  labelCarrierId: string;
  carrierDropoffLocationId: string | null;
}

export function useUpdateServingDefaultDropoff() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      dispatchCenterId,
      labelCarrierId,
      carrierDropoffLocationId,
    }: UpdateServingDefaultVars) => {
      const { data } = await api.patch<{
        data: {
          dispatchCenterId: string;
          labelCarrierId: string;
          defaultDropoffLocationId: string | null;
        };
      }>(
        "/serving/default-dropoff",
        { dispatchCenterId, labelCarrierId, carrierDropoffLocationId },
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "serving"] });
    },
  });
}

// ─── Read-only platform substrate ───────────────────────────────────────────

export interface CarrierServiceAreaAssignment {
  id: string;
  carrierId: string;
  serviceAreaId: string;
  active: boolean;
  maxDailyOrders: number | null;
  currentUtilization: number;
  serviceCoverages: CarrierServiceCoverage[];
  carrier: {
    id: string;
    code: string;
    name: string;
  };
}

export interface CarrierServiceCoverage {
  id: string;
  carrierServiceId: string;
  active: boolean;
  carrierService: Pick<
    CarrierTransportServiceRow,
    "id" | "code" | "name" | "estimatedDays"
  >;
}

export interface CarrierServiceCoverageInput {
  carrierServiceId: string;
  active: boolean;
}

export interface CarrierServiceAreaRow {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  postcodes: string[];
  maxDailyOrders: number | null;
  maxVehicles: number | null;
  carriers: CarrierServiceAreaAssignment[];
}

export interface CarrierServiceAreasResponse {
  data: CarrierServiceAreaRow[];
  pagination: OffsetPagination;
}

export interface CarrierServiceAreaPatch {
  maxDailyOrders?: number | null;
  active?: boolean;
  serviceCoverages?: CarrierServiceCoverageInput[];
}

/** `PATCH /carrier/service-areas/:id` — edit own per-area config. */
export function useUpdateCarrierServiceArea() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      patch: CarrierServiceAreaPatch;
    }) => {
      await api.patch(`/service-areas/${vars.id}`, vars.patch, {
        headers: getAuthHeaders(token),
      });
      return vars.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "service-areas"] });
    },
  });
}

/**
 * An area EZX has defined, and whether THIS carrier serves it.
 * `assignment: null` = the carrier does not serve it at all.
 */
export interface AvailableServiceArea {
  id: string;
  code: string;
  name: string;
  assignment: {
    id: string;
    active: boolean;
    maxDailyOrders: number | null;
    serviceCoverages: CarrierServiceCoverage[];
  } | null;
}

/**
 * `GET /carrier/service-areas/available` — every area the carrier COULD serve.
 *
 * Distinct from `useCarrierServiceAreas`, which lists only the areas already
 * assigned. Without this a carrier could not see an area to add it, so coverage
 * could only ever be granted by EZX from the other side.
 */
export function useAvailableServiceAreas() {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "service-areas", "available"],
    queryFn: async () => {
      const { data } = await api.get<{ data: AvailableServiceArea[] }>(
        "/service-areas/available",
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    enabled: isLoggedIn,
  });
}

export interface DeclareServiceAreaVars {
  serviceAreaId: string;
  maxDailyOrders?: number | null;
  serviceCoverages?: CarrierServiceCoverageInput[];
}

/**
 * `POST /carrier/service-areas` — declare that we serve an area.
 *
 * Upserts, so re-declaring an area switched off with `active: false` turns it
 * back on instead of colliding with the unique constraint.
 */
export function useDeclareServiceArea() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: DeclareServiceAreaVars) => {
      const { data } = await api.post<{
        data: CarrierServiceAreaAssignment;
      }>("/service-areas", body, { headers: getAuthHeaders(token) });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "service-areas"] });
    },
  });
}

export function useCarrierServiceAreas(params?: {
  limit?: number;
  offset?: number;
  /** Walk every scoped API page for complete coverage summaries/discovery. */
  all?: boolean;
}) {
  const { token, isLoggedIn } = useSession();
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const all = params?.all ?? false;
  return useQuery({
    queryKey: ["carrier", "service-areas", { limit, offset, all }],
    queryFn: async () => {
      const fetchPage = async (pageOffset: number, pageLimit: number) => {
        const { data } = await api.get<CarrierServiceAreasResponse>(
          "/service-areas",
          {
            headers: getAuthHeaders(token),
            params: { limit: pageLimit, offset: pageOffset },
          }
        );
        return data;
      };

      if (!all) return fetchPage(offset, limit);
      return fetchAllOffsetPages(fetchPage);
    },
    enabled: isLoggedIn,
  });
}

export type CarrierAddOnServiceType =
  | "PICKUP"
  | "PACKAGING"
  | "INSURANCE"
  | "SIGNATURE"
  | "SPECIAL_HANDLING";

export interface CarrierProviderAddOnService {
  id: string;
  providerCarrierId: string;
  type: CarrierAddOnServiceType;
  code: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  provider: {
    id: string;
    code: string;
    name: string;
  };
  pickupDetails: {
    dimFactorCm: string | number | null;
    dimFactorInch: string | number | null;
  } | null;
  insuranceDetails: Record<string, unknown> | null;
  packagingDetails: Record<string, unknown> | null;
  signatureDetails: Record<string, unknown> | null;
  specialHandlingDetails: Record<string, unknown> | null;
  labelCarrierAccess: Array<{
    id: string;
    enabled: boolean;
    labelCarrier: {
      id: string;
      code: string;
      name: string;
    };
  }>;
}

export interface CarrierProviderAddOnServicesResponse {
  success: boolean;
  data: CarrierProviderAddOnService[];
  pagination: OffsetPagination;
}

export interface CreateCarrierAddOnServiceInput {
  type: "PICKUP";
  code: string;
  name: string;
  active?: boolean;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
}

export interface UpdateCarrierAddOnServiceInput {
  active?: boolean;
  name?: string;
  dimFactorCm?: number | null;
  dimFactorInch?: number | null;
}

/** Create a pickup service owned by the authenticated provider carrier. */
export function useCreateCarrierAddOnService() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateCarrierAddOnServiceInput) => {
      const { data } = await api.post<{ data: CarrierProviderAddOnService }>(
        "/add-on-services",
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "add-on-services"] });
    },
  });
}

/** Update a carrier-owned add-on header and pickup DIM configuration. */
export function useUpdateCarrierAddOnService() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      patch: UpdateCarrierAddOnServiceInput;
    }) => {
      const { data } = await api.patch<{
        data: CarrierProviderAddOnService;
      }>(`/add-on-services/${vars.id}`, vars.patch, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carrier", "add-on-services"] });
    },
  });
}

export function useCarrierProviderAddOnServices(params?: {
  type?: CarrierAddOnServiceType;
  active?: boolean;
  limit?: number;
  offset?: number;
  /** Walk every scoped API page for complete catalogue discovery. */
  all?: boolean;
}) {
  const { token, isLoggedIn } = useSession();
  const type = params?.type;
  const active = params?.active;
  const limit = params?.limit ?? 20;
  const offset = params?.offset ?? 0;
  const all = params?.all ?? false;
  return useQuery({
    queryKey: [
      "carrier",
      "add-on-services",
      { type: type ?? null, active: active ?? null, limit, offset, all },
    ],
    queryFn: async () => {
      const fetchPage = async (pageOffset: number, pageLimit: number) => {
        const { data } = await api.get<CarrierProviderAddOnServicesResponse>(
          "/add-on-services",
          {
            headers: getAuthHeaders(token),
            params: {
              ...(type && { type }),
              ...(active !== undefined ? { active } : {}),
              limit: pageLimit,
              offset: pageOffset,
            },
          }
        );
        return data;
      };

      if (!all) return fetchPage(offset, limit);
      return fetchAllOffsetPages(fetchPage);
    },
    enabled: isLoggedIn,
  });
}

export interface CarrierPickupRateBand {
  id: string;
  /** Sent by the Server (include, not select); echoed back as the save basis. */
  updatedAt: string;
  carrierAddOnServiceId: string;
  fromServiceAreaId: string;
  dispatchCenterId: string;
  weightFromKg: string | number;
  weightToKg: string | number;
  baseRateCents: number;
  perKgCents: number | null;
  anchorWeightKg: string | number | null;
  rushHourSurchargeCents: number | null;
  currencyCode: string;
  fromServiceArea?: { id: string; code: string; name: string } | null;
  dispatchCenter?: { id: string; code: string; name: string } | null;
}

export interface CarrierPickupRateBandInput {
  fromServiceAreaId: string;
  dispatchCenterId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents: number | null;
  currencyCode?: string;
}

/** A stored row as the client read it — see `basis` below. */
export interface RateBandBasisRow {
  id: string;
  updatedAt: string;
}

export interface AtomicSaveCarrierPickupRateBandsInput {
  carrierAddOnServiceId: string;
  dispatchCenterId: string;
  fromServiceAreaIds: string[];
  bands: CarrierPickupRateBandInput[];
  /**
   * REQUIRED by the Server (ezx-server #580): the rows this payload was built
   * from, for THIS (service, centre, from-areas) slice. The save replaces the
   * whole slice, so without a basis a second editor's save silently reverts the
   * first — and the reverted edit is a price. A stale basis is refused with 409.
   */
  basis: RateBandBasisRow[];
}

export function useCarrierPickupRateBands(
  carrierAddOnServiceId: string | null | undefined,
  dispatchCenterId: string | null | undefined
) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: [
      "carrier",
      "pickup-rate-bands",
      carrierAddOnServiceId,
      dispatchCenterId,
    ],
    queryFn: async () => {
      const { data } = await api.get<{ data: CarrierPickupRateBand[] }>(
        "/pricing/pickup-rate-bands",
        {
          headers: getAuthHeaders(token),
          params: { carrierAddOnServiceId, dispatchCenterId },
        }
      );
      return data.data;
    },
    enabled: isLoggedIn && !!carrierAddOnServiceId && !!dispatchCenterId,
  });
}

export function useAtomicSaveCarrierPickupRateBands() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AtomicSaveCarrierPickupRateBandsInput) => {
      const { data } = await api.post<{ data: CarrierPickupRateBand[] }>(
        "/pricing/pickup-rate-bands/atomic-save",
        body,
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [
          "carrier",
          "pickup-rate-bands",
          vars.carrierAddOnServiceId,
          vars.dispatchCenterId,
        ],
      });
    },
  });
}

export interface CarrierAddOnServiceEnablement {
  labelCarrierId: string;
  labelCarrier: { id: string; code: string; name: string };
  enabled: boolean;
}

export function useCarrierAddOnServiceEnablement(serviceId: string) {
  const { token, isLoggedIn } = useSession();
  return useQuery({
    queryKey: ["carrier", "add-on-services", serviceId, "enablement"],
    queryFn: async () => {
      const { data } = await api.get<{
        data: CarrierAddOnServiceEnablement[];
      }>(`/add-on-services/${serviceId}/enablement`, {
        headers: getAuthHeaders(token),
      });
      return data.data;
    },
    enabled: isLoggedIn && !!serviceId,
  });
}

export function useUpdateCarrierAddOnServiceEnablement() {
  const { token } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      serviceId: string;
      labelCarrierId: string;
      enabled: boolean;
    }) => {
      const { data } = await api.put<{
        data: CarrierAddOnServiceEnablement;
      }>(
        `/add-on-services/${vars.serviceId}/enablement`,
        { labelCarrierId: vars.labelCarrierId, enabled: vars.enabled },
        { headers: getAuthHeaders(token) }
      );
      return data.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: ["carrier", "add-on-services", vars.serviceId, "enablement"],
      });
    },
  });
}
